/**
 * AI RP (Replenishment Planning) Scenario Planning
 * RP 모듈 전용 - 현황 분석 · 시나리오 구성 · 시나리오 비교 · 보충계획 확정
 * AiScenarioPlanning.js 가 이 파일에서 RP_MODULE 을 import 함
 *
 * 4탭 워크플로우 (전 모듈 공통):
 *   Tab 0 — 현황 분석  (RpExtraInitContent + KPI 요약)
 *   Tab 1 — 시나리오 구성  (시나리오 생성[표준/직접/AI] → 계획 파라미터 · 엔진 실행)
 *   Tab 2 — 시나리오 비교  (KPI 비교)
 *   Tab 3 — 보충계획 확정
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box, Card, CardContent, Chip, CircularProgress, IconButton, LinearProgress,
  Paper, Stack, Typography,
} from "@mui/material";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import i18n from "@plannel/utils/i18n";
import { reloadRpVersionFilter } from "@plannel/components/filter/RpVersionFilter";
import { hasEffectiveValue, paramRowSx, paramGroupHeaderSx, paramGroupRuleSx,
         PARAM_ROW_RADIUS, PARAM_CARD_RADIUS, PARAM_ROW_PAD_X,
         PARAM_NUM_INPUT_W, PARAM_SELECT_W, isInlineEditable,
         PARAM_ROW_MIN_H, PARAM_TOGGLE_SX, rememberModuleCatalog } from "./scenarioParamUtil";
import { fmtParamValue, baselineCurrentText, currentPrefixFor, useEditEndGuard,
         ParamValueCells, ParamRevertButton } from "./scenarioParamCells";
import ScenarioParamAddPicker from "./ScenarioParamAddPicker";
import { cellText } from "./aiFieldText";
import LocalShippingIcon  from "@mui/icons-material/LocalShipping";
import ErrorOutlineIcon   from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon     from "@mui/icons-material/ExpandMore";
import scenarioService from "../../services/ai/scenarioService";
import rpScenarioAiService from "../../services/rp/rp-scenario-ai-service";
import { StatusSection, StatusKpiSection, NavName, ForecastActualChart,
         StatusItemHeader, StatusItemRow, StatusItemList, statusListH,
         StatusEmpty, StatusEmptyPanel, StatusPendingTag, KPI_EMPTY_DASH, BarTip,
         buildStatusPanelSummary, useLastRunSummary,
         StatusToolbar, StatusToolbarSelect, STATUS_TOOLBAR_BLOCK_H } from "./scenarioStatusUi";
import { FONT } from "@plannel/components/ai/typographyTokens";
import AiSegmentedControl from "./AiSegmentedControl";
import { FeasibilityHeroBadges, feasWarnCount } from "./CompareFeasibilityWarnings";
import { C } from "@plannel/components/ai/aiTokens";
import { fmtMoney } from "./aiMoney";

// 현황 미집계 → 보충계획 실행 화면(RUN_REPLENISHMENT_PLAN, TabMenuList key 2100).
//   ⚠️ RunRp 가 읽는 viewState 키는 `version` 이다(RP_REVIEW 의 `rpVersion` 과 다르다).
//   버전 필터 redux 캐시는 무효화해 실행 화면이 최신 버전 목록을 다시 읽게 한다.
export function openRunRp(dispatch, version) {
  const VIEW = "RUN_REPLENISHMENT_PLAN";
  reloadRpVersionFilter(dispatch);
  dispatch(reduxUtil.removeViewState({ viewName: VIEW }));
  if (version?.id != null) {
    dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(VIEW, {
      version: { id: version.id, name: String(version.name ?? "") },
    })));
  }
  dispatch(reduxUtil.updateHistoryState(VIEW));
}

// 현황 문제항목 → 보충계획 결과(RP_REVIEW): 버전 + 도착거점 + 단일품목 자동 조회
export function openRpReview(dispatch, cond, version) {
  const { locCd, itemCd } = cond || {};
  // 버전만 지정(시나리오 산점도 클릭)해도 오픈 허용 — 필터 없이 해당 버전 전체 결과 표시.
  if (!locCd && !itemCd && version?.id == null) return;
  const VIEW = "RP_REVIEW";
  const payload = { currentPage: 1 };
  if (version?.id != null) payload.rpVersion = { id: version.id, name: String(version.name ?? "") };
  payload.filter = {
    originLocation: null,
    destLocation: locCd ? [{ locCd: String(locCd), locName: String(locCd) }] : null,
    category: null,
  };
  if (itemCd) payload.itemFilterList = { itemCd: [String(itemCd)] };
  dispatch(reduxUtil.removeViewState({ viewName: VIEW }));
  dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(VIEW, payload)));
  dispatch(reduxUtil.updateHistoryState(VIEW));
}


// 현황 리스트 우측 칩 열 폭 — 실측 동기화(StatusItemList) 전 첫 페인트용 폴백값.
// 실제 폭은 전 행의 칩 실측 최대값으로 맞춰진다(언어별 재산정 불필요).
const SHORT_CHIP_W  = 82;  // 부족/지연 발생 기간 ("부족 6개월" — 단일 칩)
// 결품 칩 + LT 윈도 칩 — 행마다 결품 칩 유무가 갈리므로 슬롯을 2개로 고정한다.
// 슬롯별로 폭이 실측 동기화돼 칩이 행 사이에서 좌우로 흔들리지 않는다.
const URGENT_CHIP_W = [44, 60];
// 주요 이슈/문제점 행 — 수치 셀 폭 / 우측 KPI 칩 열 폭 (IP 재고 이슈 진단과 동일 규격)
const ISSUE_CNT_W      = 64;
const ISSUE_KPI_CHIP_W = 92;

// ── 숫자 포맷 헬퍼 ───────────────────────────────────────────────────────────
// ⚠️ 값이 없으면 "--" 같은 문자열을 만들지 않고 **null** 을 반환한다. 미집계 표기('—',
//    단위·배지·상태색 제거)는 StatusKpiCard 가 단독으로 소유한다(전 모듈 동일 규칙).
//    0 은 실측값이므로 정상 포맷한다 — 미집계와 구분되어야 한다.
function fmtFillRate(v) {
  if (v == null || v === "") return null;
  return Number(v).toFixed(1);
}
function fmtQty(v) {
  if (v == null || v === "") return null;
  return Math.round(Number(v)).toLocaleString();
}
const fmtAmt = fmtMoney;
function fmtDays(v) {
  if (v == null || v === "") return null;
  return Number(v).toFixed(1);
}

// ── 이슈 스타일 ──────────────────────────────────────────────────────────────
// 문제 항목/과잉재고 섹션과 색감 통일 — 선명한 단일 강조색(바·칩·숫자 공용) + 연한 배경.
const ISSUE_STYLE = {
  error:   { color: C.g3,  bg: C.g3l,  text: C.g3t  },
  warning: { color: C.g2,  bg: C.g2l,  text: C.g2t  },
  info:    { color: C.acc, bg: C.accL, text: C.accT },
};
// 이슈 카드 정렬(심각도 오름차순 = error 먼저).
const SEV_RANK = { error: 0, warning: 1, info: 2 };
// 다른 섹션(문제 항목 TOP5 등)과 통일해 이슈 카드도 최대 5개까지만 노출(severity 상위).
const RP_ISSUE_MAX = 5;
// 좌측 추이 차트 높이를 우측 이슈 카드 목록의 실측 높이에 맞춘다(둘 다 height:100% 라
// grid row 는 큰 쪽에 맞춰지는데, 차트 ResponsiveContainer 는 고정 높이라 이슈가 길면
// 차트 아래에 빈 공간이 생긴다). 카드 높이는 desc 줄바꿈·화면폭·언어에 따라 달라져
// 상수 추정이 어긋나므로 ResizeObserver 로 이슈 목록 컨테이너 높이를 직접 잰다.
//   RP_CHART_PAD_DIFF_PX : 차트 content 상하 패딩(14×2) − 이슈 content 패딩(10×2). 두
//                          카드의 자연 높이를 맞추려면 차트 내부를 이슈 목록보다 이만큼 줄인다.
//   RP_CHART_MIN_H       : 이슈가 적거나 로딩일 때의 하한 — 이슈 카드 4개짜리 목록 높이 기준
//                          (BF 모델 분포 차트가 4행을 하한으로 잡는 것과 같은 규칙)
const RP_CHART_PAD_DIFF_PX = 8;
const RP_CHART_MIN_H = statusListH(4) - RP_CHART_PAD_DIFF_PX;
// ── 목표 대비 차이 배지 (KPI 카드 footerRight) ───────────────────────────────
// RP는 실시간 정보가 없어 카드 본값=최종 릴리즈값. 이 배지는 "릴리즈값이 목표를
// 얼마나 벗어났는지"(delta = 릴리즈 − 목표)를 ▲/▼ 컬러로 표기한다.
//  higherWorse=false : 오를수록 양호(서비스 충족률) — delta>0 양호(green)
//  higherWorse=true  : 오를수록 악화(리드타임)     — delta>0 악화(warn)
// badTone: 악화 시 색({c,bg}). 카드 본체 색과 맞추기 위해 주입(기본 red=g3, 리드타임 등은 g2).
function GapBadge({ delta, higherWorse, fmt, okNote, badNote, badTone = { c: C.g3, bg: C.g3l },
                   bg, prefix }) {
  const { t } = useTranslation();
  if (delta == null || !isFinite(delta)) return null;
  const good = delta === 0 ? true : (higherWorse ? delta < 0 : delta > 0);
  const sty  = good ? { c: C.g1, bg: C.g1l } : badTone;
  const sign  = delta > 0 ? "+" : delta < 0 ? "-" : "";
  const arrow = delta > 0 ? " ▲" : delta < 0 ? " ▼" : "";
  const note  = delta === 0 ? t("kpiAchievedLabel") : good ? okNote : badNote;
  const resolvedPrefix = prefix ?? `${t("vsTargetLabel")} `;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center",
               px: 0.6, py: 0, borderRadius: 1, bgcolor: bg ?? sty.bg, flexShrink: 0 }}>
      <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: sty.c, lineHeight: 1.4,
                        whiteSpace: "nowrap" }}>
        {resolvedPrefix}{sign}{fmt(Math.abs(delta))}{arrow} {note}
      </Typography>
    </Box>
  );
}

// 미집계 빈 상태 패널은 전 모듈 공용(StatusEmptyPanel, scenarioStatusUi.js)으로 승격했다.
// RP 는 문구(제목·설명·CTA 라벨)와 CTA 목적지만 주입한다.

// ── RP 현황 조회 전용 컴포넌트 ──────────────────────────────────────────────
// AiScenarioPlanning.js 의 createMode Step 0 에서 mod.ExtraInitContent 로 렌더됨
// eslint-disable-next-line no-unused-vars
export function RpExtraInitContent({ mod, phase, versionInfo, cachedData, onDataChange, onReadyChange }) {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const [currentState, setCurrentState] = useState(cachedData ?? null);
  const [loading, setLoading]           = useState(false);
  const [fetchError, setFetchError]     = useState(null);

  const verId = versionInfo?.id ?? null;
  // 부모의 getLatestVersion 결과 대기 중(undefined=미조회, null=조회중). 조회 완료 전엔 verId=null.
  const verResolving = versionInfo === undefined || versionInfo === null;

  // 최종 릴리즈 결과(z_v_ai_rp) 미적재 — 버전은 있으나 결과 데이터가 없는 상태.
  const releasedNone  = currentState?.released_fact_available === false;
  // 미집계 = 릴리즈 버전이 없거나(!verId) 결과가 적재되지 않은 상태.
  //   ⚠️ 이 화면의 **단일 판정 기준**이다. 미집계면 릴리즈 fact 에서 파생되지 않은 보조 데이터
  //   (정책 이슈 등)까지 함께 감춘다 — 일부만 값이 남으면 판정 가능 여부를 오독하게 된다.
  const notAggregated = !verId || releasedNone;

  // 대상 화면이 권한 밖이면 링크를 만들지 않는다(NavName 이 함수 아닌 onClick 을 걸러낸다).
  const canDrillReview = isPermitted("RP_REVIEW");

  // 현황 문제항목 카드 → 보충계획 결과(RP_REVIEW) 드릴다운(버전+거점+품목)
  const goToRpReview = useCallback(
    (row) => openRpReview(reduxDispatch, { locCd: row.loc_cd, itemCd: row.item_cd }, versionInfo),
    [reduxDispatch, versionInfo],
  );

  const load = useCallback(() => {
    if (!verId) return;
    setLoading(true);
    setFetchError(null);
    scenarioService
      .getRpInitialState()
      .then(cs => {
        const state = cs?.data ?? cs;
        setCurrentState(state);
      })
      .catch(() => setFetchError(t("MSG_RpStatusQueryFailed")))
      .finally(() => setLoading(false));
  }, [verId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 캐시가 없을 때만 조회
  useEffect(() => {
    if (cachedData) return;
    if (verId && currentState === null && !loading) {
      load();
    }
  }, [verId, currentState, loading, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // 데이터 변경 시 부모에 캐시 전달
  useEffect(() => {
    if (currentState) onDataChange?.(currentState);
  }, [currentState]); // eslint-disable-line react-hooks/exhaustive-deps

  // 최근 실행 기록(빈 상태 패널 우측 요약) — 전 모듈 공용 훅. 미집계일 때만 1건 조회한다.
  //   ⚠️ 훅이라 early return 보다 앞에 있어야 한다(훅 순서 규칙).
  const lastRun = useLastRunSummary("RP", !verResolving && notAggregated);

  // 조회 완료 여부를 부모에 보고 → 상단 액션바(자동 수행 / 시나리오 구성으로) 잠금 해제 기준.
  //   RP 만 버전 게이트가 하나 더 있다 — 버전 조회 중(verResolving)에는 아직 시작도 못 한 상태이고,
  //   릴리즈 버전이 아예 없으면(!verId) 조회는 영영 일어나지 않으므로 로딩이 아니라 "완료"로 본다
  //   (화면은 rpNoReleasedPlanNotice 를 표시한다). 실패(fetchError)도 완료로 본다.
  useEffect(() => {
    onReadyChange?.(!verResolving && !loading && (!verId || !!currentState || !!fetchError));
  }, [verResolving, verId, loading, currentState, fetchError]); // eslint-disable-line react-hooks/exhaustive-deps

  // 월별 수요·발주·예상재고 추이 — 전사+카테고리별 일괄 보유, 드롭다운 로컬 전환(재조회 없음)
  const [trendGroup, setTrendGroup] = useState("");   // "" = 전사 총량
  const selectedTrend = useMemo(() => {
    const td = currentState?.replenishment_trend;
    if (!td) return null;
    return trendGroup ? (td.by_group?.[trendGroup] ?? { data: [], series: [] }) : td.all;
  }, [currentState, trendGroup]);
  // 전사 데이터 존재 여부로 섹션 노출 판단
  const trendHasData = !notAggregated
    && (currentState?.replenishment_trend?.all?.data?.length || 0) > 0;
  // 카테고리 드롭다운 옵션 (category_cd → category_name), 이름 정렬
  const trendGroupOptions = useMemo(() => {
    const labels = currentState?.replenishment_trend?.group_labels || {};
    return Object.entries(labels).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [currentState]);
  // 툴바(조회 범위) 행이 본문 최상단을 차지한 만큼 차트를 줄여, 우측 이슈 카드와 총 높이를 맞춘다.
  const trendToolbarH = trendGroupOptions.length > 0 ? STATUS_TOOLBAR_BLOCK_H : 0;

  // ── KPI 카드 구성 ────────────────────────────────────────────────────────
  const kpi         = notAggregated ? {} : (currentState?.kpi_current ?? {});
  const hasKpi      = currentState != null;
  const fillRate    = kpi.avg_fill_rate_pct;
  const orderQty    = kpi.total_order_qty;
  const ltDays      = kpi.avg_lead_time_days;
  const orderCost   = kpi.total_order_cost;
  const avgEohQty   = kpi.avg_eoh_qty;
  const avgEohAmt   = kpi.avg_eoh_amt;
  const fillRateOk  = fillRate != null && Number(fillRate) >= 95;
  const ltOk        = ltDays   != null && Number(ltDays)   <= 12;

  // 목표 대비 릴리즈 차이(delta = 릴리즈 − 목표). **집계값이 있을 때만** 계산한다 —
  // 미집계 상태에서 0 을 실적으로 대입하면 "-95.0%p ▼ 미달" 같은 거짓 판정이 나온다.
  // 목표치 자체는 카드 하단(footer)에 참조 정보로 계속 남는다.
  const FILL_TARGET = 95;   // 공급 충족률 목표(%)
  const LT_TARGET   = 12;   // 평균 리드타임 목표(일)
  const fillGap = fillRate != null ? Number(fillRate) - FILL_TARGET : null;
  const ltGap   = ltDays   != null ? Number(ltDays)   - LT_TARGET   : null;

  // 카드 값은 미집계면 null(문자열 폴백 금지) — 표기·단위·색은 StatusKpiCard 가 결정한다.
  const kpiItems = [
    {
      label: t("rpKpiFillRateLabel"),   key: "fill_rate",
      value: hasKpi ? fmtFillRate(fillRate) : null,
      unit:  t("percentUnit"),
      target: "95%",
      color: fillRateOk ? C.g1 : C.g3,
      valueRight: fillGap != null
        ? <GapBadge delta={fillGap} higherWorse={false} fmt={(v) => `${v.toFixed(1)}%p`}
                    okNote={t("kpiAchievedLabel")} badNote={t("notMetLabel")} bg="transparent" prefix="" />
        : null,
    },
    {
      label: t("rpKpiTotalOrderQtyLabel"),     key: "total_order_qty",
      value: hasKpi ? fmtQty(orderQty) : null,
      unit:  "EA",
      target: null,
      color: C.acc,
      subLabel: t("replenishPlanAmt"),
      subValue: hasKpi ? fmtAmt(orderCost) : null,
    },
    {
      label: t("rpKpiAvgLeadTimeLabel"), key: "avg_lt",
      value: hasKpi ? fmtDays(ltDays) : null,
      unit:  t("dayUnit"),
      target: `12${t("dayUnit")}`,
      color: ltOk ? C.g1 : C.g2,
      valueRight: ltGap != null
        ? <GapBadge delta={ltGap} higherWorse fmt={(v) => `${v.toFixed(1)}${t("dayUnit")}`}
                    okNote={t("shortenedLabel")} badNote={t("greaterThan")} badTone={{ c: C.g2, bg: C.g2l }} bg="transparent" prefix="" />
        : null,
    },
    {
      label: t("rpKpiAvgEohQtyLabel"),   key: "avg_eoh_qty",
      value: hasKpi ? fmtQty(avgEohQty) : null,
      unit:  "EA",
      target: null,
      color: C.g2,
      subLabel: t("rpKpiAvgEohAmtLabel"),
      subValue: hasKpi ? fmtAmt(avgEohAmt) : null,
    },
  ];

  // 미충족 수량 분류: "delay"(버전 BACKORDER=allow) | "short"(그 외). 버전 단위 단일.
  // (이슈 카드 desc 에서 참조하므로 issueItems 조립보다 먼저 선언)
  const shortDelayKind = currentState?.short_delay_kind ?? "short";
  const isDelay        = shortDelayKind === "delay";
  const sdLabel        = isDelay ? "Delay" : "Short";
  // 발생 기간 칩 문구 — fact 의 기간 카운트를 버전 버켓 단위(day/week/month)로 읽어준다.
  // 버켓을 모르면(null) 단위 없이 "부족 6회" 로 폴백 — '일/개월' 을 추정하면 오표기가 된다.
  const bucketChipKey  = { day:   "rpShortPeriodDayChip",
                           week:  "rpShortPeriodWeekChip",
                           month: "rpShortPeriodMonthChip" }[currentState?.bucket_unit]
                         ?? "rpShortPeriodCountChip";

  // ── 이슈 목록 ────────────────────────────────────────────────────────────
  // 백엔드가 준 신호 카운트를 조건 분기해 카드로 조립하고 severity 순으로 정렬해 전량 노출한다.
  //
  // ⚠️ real_stockout(실결품)과 severe_safety_short(안전재고 심각 미달)는 백엔드에서
  //    HIGH 위험을 배타 분할한 값이다(합 = high_risk_count). 두 카드가 같은 SKU를
  //    중복 계상하지 않는다. medium_risk_count 는 그와 별개 집합.
  // ⚠️ 미집계면 이슈 신호도 전부 감춘다.
  //   (2026-08: policy_issue_summary(z_rp_policy) 기반 카드 2종 — ROP 오설정 / 주기재고
  //    미적용 — 을 제거해 현황을 팩트 단일 소스로 맞췄다. "릴리즈 fact 없이도 값이 나오는"
  //    반쪽 화면 문제도 함께 사라진다. 두 지표는 RP 정책 AI 최적화 화면에 그대로 있다.)
  const issueSummary       = notAggregated ? null : (currentState?.issue_summary ?? null);
  const riskSummary        = notAggregated ? null : (currentState?.risk_summary  ?? null);
  const allIssueItems = (() => {
    if (!issueSummary) return [];
    const n = (v) => v ?? 0;
    const realStockout  = n(issueSummary.real_stockout);
    const urgentShort   = n(issueSummary.urgent_shortage);
    const noOrderShort  = n(issueSummary.no_order_short);
    const severeSafety  = n(issueSummary.severe_safety_short);
    const mediumRisk    = n(riskSummary?.medium_risk_count);
    const shortDemand   = n(issueSummary.short_demand);
    const lowCoverage   = n(issueSummary.low_coverage);
    const excessCount   = n(issueSummary.excess_inv);
    const longLtCount   = n(issueSummary.long_lead_time);
    const moqOversize   = n(issueSummary.moq_oversize);

    const items = [];
    if (realStockout > 0) {
      items.push({
        level: "error",
        title: t("rpIssueRealStockoutTitle"),
        desc:  t("rpIssueRealStockoutDesc", { count: realStockout }),
        kpiLabel: t("rpIssueRealStockoutKpiLabel"), affectedCount: realStockout,
      });
    }
    if (urgentShort > 0) {
      items.push({
        level: "error",
        title: t("rpIssueUrgentShortageTitle"),
        desc:  t("rpIssueUrgentShortageDesc", { count: urgentShort }),
        kpiLabel: t("rpIssueUrgentShortageKpiLabel"), affectedCount: urgentShort,
      });
    }
    if (noOrderShort > 0) {
      items.push({
        level: "error",
        title: t("rpIssueNoOrderShortTitle"),
        desc:  t("rpIssueNoOrderShortDesc", { count: noOrderShort }),
        kpiLabel: t("rpIssueNoOrderShortKpiLabel"), affectedCount: noOrderShort,
      });
    }
    if (severeSafety > 0) {
      items.push({
        level: "warning",
        title: t("rpIssueSevereSafetyTitle"),
        desc:  t("rpIssueSevereSafetyDesc", { count: severeSafety }),
        kpiLabel: t("safetyStockLabel"), affectedCount: severeSafety,
      });
    }
    if (mediumRisk > 0) {
      items.push({
        level: "warning",
        title: t("rpIssueStockoutTitle"),
        desc:  t("rpIssueMediumSafetyDesc", { count: mediumRisk }),
        kpiLabel: t("safetyStockLabel"), affectedCount: mediumRisk,
      });
    }
    if (shortDemand > 0) {
      items.push({
        level: "warning",
        title: t("rpIssueShortDemandTitle"),
        desc:  t("rpIssueShortDemandDesc", { count: shortDemand, kind: sdLabel }),
        kpiLabel: t("rpIssueShortDemandKpiLabel"), affectedCount: shortDemand,
      });
    }
    // 충족률은 SKU 카운트가 아니라 비율 지표 — fillRate 0 은 "미달"이 아니라 "데이터 없음".
    if (fillRate != null && Number(fillRate) > 0 && Number(fillRate) < 95) {
      const gap = Number((95 - Number(fillRate)).toFixed(1));
      items.push({
        level: gap > 5 ? "warning" : "info",
        title: t("rpIssueFillRateTitle"),
        desc:  t("rpIssueFillRateDesc", { rate: Number(fillRate).toFixed(1), gap }),
        kpiLabel: t("rpKpiFillRateLabel"),
        affectedCount: Number(Number(fillRate).toFixed(1)), unit: "%",
      });
    }
    if (lowCoverage > 0) {
      items.push({
        level: "warning",
        title: t("rpIssueLowCoverageTitle"),
        desc:  t("rpIssueLowCoverageDesc", { count: lowCoverage }),
        kpiLabel: t("rpIssueLowCoverageKpiLabel"), affectedCount: lowCoverage,
      });
    }
    if (excessCount > 0) {
      items.push({
        level: "warning",
        title: t("rpIssueExcessTitle"),
        desc:  t("rpIssueExcessDesc", { count: excessCount }),
        kpiLabel: t("excessInvIssueTitle"), affectedCount: excessCount,
      });
    }
    if (longLtCount > 0) {
      items.push({
        level: "info",
        title: t("rpIssueLongLtTitle"),
        desc:  t("rpIssueLongLtDesc", { count: longLtCount }),
        kpiLabel: t("rpIssueLeadTimeKpiLabel"), affectedCount: longLtCount,
      });
    }
    if (moqOversize > 0) {
      items.push({
        level: "info",
        title: t("rpIssueMoqOversizeTitle"),
        desc:  t("rpIssueMoqOversizeDesc", { count: moqOversize }),
        kpiLabel: t("rpIssueMoqKpiLabel"), affectedCount: moqOversize,
      });
    }
    // ⚠️ 이슈가 하나도 없을 때 "이슈 없음 / 정상 / 0 SKU" 합성 행을 넣지 않는다.
    //    집계가 없는 구간에서 그 행은 "문제가 없다"는 거짓 확인이 된다.
    //    빈 목록은 호출부가 StatusEmpty(중립·판정 불가)로 렌더한다.
    items.sort((a, b) => SEV_RANK[a.level] - SEV_RANK[b.level]);
    return items;
  })();
  // severity 상위 최대 RP_ISSUE_MAX 개만 노출(측정·렌더 공통).
  const visibleIssueItems = allIssueItems.slice(0, RP_ISSUE_MAX);
  // 좌측 추이 차트 높이 = 우측 이슈 목록 컨테이너 실측 높이 − 패딩차(하한 RP_CHART_MIN_H).
  // 콜백 ref 로 이슈 목록 Box 를 ResizeObserver 에 물려 개수·줄바꿈 변화에 자동 추종한다.
  const [chartTrendHeight, setChartTrendHeight] = useState(RP_CHART_MIN_H);
  const issueRoRef = useRef(null);
  const measureIssueBox = useCallback((el) => {
    if (issueRoRef.current) { issueRoRef.current.disconnect(); issueRoRef.current = null; }
    if (!el) return;
    const update = () => setChartTrendHeight(
      Math.max(RP_CHART_MIN_H, Math.round(el.offsetHeight - RP_CHART_PAD_DIFF_PX)),
    );
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    issueRoRef.current = ro;
  }, []);

  // 표준 골격: 주요 현황 목록은 공통 키 highlights (구 critical_items)
  const criticalItems   = notAggregated ? null : (currentState?.highlights        ?? null);
  const shortDelayItems = notAggregated ? null : (currentState?.short_delay_items ?? null);

  // 문제 항목 TOP5 양쪽 목록이 모두 비어 있으면 섹션 전체가 "집계 대기" 상태다.
  const topItemsPending = !loading
    && (!shortDelayItems || shortDelayItems.length === 0)
    && (!criticalItems   || criticalItems.length === 0);

  // 버전 조회중 또는 initial-state 로딩중 — IP/MP 와 동일하게 전체를 단일 '현황 분석 중...' 카드로 표시.
  // (verResolving 을 포함해 버전 조회 완료 전 rpNoReleasedPlanNotice 가 순간 깜빡이는 현상 제거)
  if (verResolving || loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: "16px !important" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} sx={{ color: C.acc }} />
            <Typography variant="caption" color="text.secondary">
              {t("rpStatusAnalyzingLabel")}
            </Typography>
          </Stack>
          <LinearProgress sx={{ mt: 1.5, borderRadius: 2, height: 3 }} />
        </CardContent>
      </Card>
    );
  }

  // ⚠️ 릴리즈 버전이 없어도 화면을 대체 카드 하나로 축소하지 않는다 —
  //    빈 상태 패널 + 정상 골격(KPI '—' / 스켈레톤)을 그대로 렌더해 레이아웃이 튀지 않게 한다.

  if (fetchError) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${C.g3}30` }}>
        <CardContent sx={{ p: "10px 14px !important" }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <ErrorOutlineIcon sx={{ fontSize: 15, color: C.g3t }} />
            <Typography sx={{ fontSize: FONT.SUB, color: C.g3t }}>{fetchError}</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={1.5}>

      {/* 미집계 빈 상태 패널 — 릴리즈 버전 없음 / fact 미적재 두 상태를 하나로 처리.
          화면에서 유일한 solid CTA 를 갖는다(툴바의 '시나리오 구성으로'는 secondary 로 강등). */}
      {notAggregated && (() => {
        const verName   = currentState?.plan_ver_name ?? versionInfo?.name ?? null;
        const releaseTs = currentState?.plan_release_ts ?? versionInfo?.releaseTs ?? null;
        return (
          <StatusEmptyPanel
            title={t("statusNoAggregatedResultTitle")}
            desc={verId
              ? t("rpNoAggregatedResultDesc", { name: verName || KPI_EMPTY_DASH })
              : t("rpNoReleasedVersionDesc")}
            ctaLabel={t("rpRunPlanCtaLabel")}
            onCta={isPermitted("RUN_REPLENISHMENT_PLAN") ? () => openRunRp(reduxDispatch, versionInfo) : undefined}
            summary={buildStatusPanelSummary(t, {
              verName,
              releaseTs: releaseTs ? new Date(releaseTs).toLocaleDateString() : null,
              lastRun,
            })}
          />
        );
      })()}

      {/* ── 현황 KPI 요약 ─────────────────────────────────────────────────── */}
      <StatusKpiSection
        collapsible
        defaultOpen
        versionInfo={versionInfo}
        right={loading
          ? <CircularProgress size={12} sx={{ color: C.acc }} />
          : (notAggregated ? <StatusPendingTag /> : null)}
        /* 안내 문구는 상단 패널이 단독으로 담당한다 — 같은 내용을 배너로 중복 노출하지 않는다.
           카드는 사라지지 않고 '—' 로 남아 정상 상태와 같은 골격을 유지한다. */
        cards={kpiItems.map((item) => ({
          key: item.key,
          label: item.label,
          value: item.value,
          unit: item.unit,
          color: item.color,
          loading: loading && !hasKpi,
          footer: item.target
            ? `${t("target")} ${item.target}`
            : item.subLabel
              // 보조 수치도 미집계면 '—' — 0 으로 접지 않는다(KPI_EMPTY_DASH 와 동일 표기).
              ? `${item.subLabel} ${loading && !hasKpi ? "…" : (item.subValue ?? KPI_EMPTY_DASH)}`
              : null,
          valueRight: item.valueRight ?? null,
        }))}
      />

      {/* ── 추이 + 주요 이슈 (좌우 2열) ─────────────────────────────────────
           좌: 월별 수요·발주·예상재고 추이 + 안전재고 기준선 (품목 카테고리 드롭다운)
           우: 주요 이슈 / 문제점
           ⚠️ 2열 골격은 데이터 유무와 무관하게 고정한다 — 추이가 없다고 섹션을 지우면
              주요 이슈가 전폭으로 튀고 아래쪽이 통째로 빈 여백이 된다. 데이터가 없으면
              섹션은 자리를 지키고 내부만 스켈레톤(StatusEmpty)으로 채운다. */}
      <Box sx={{ display: "grid", gap: 1.5,
                 gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>

        <StatusSection
          collapsible
          defaultOpen
          collapseDisabled={!trendHasData}
          title={t("rpReplenishTrendTitle")}
          right={!trendHasData ? <StatusPendingTag /> : undefined}
          cardSx={{ height: "100%" }}
          contentSx={{ p: "14px 15px !important" }}
          // 헤더는 제목 + 접힘 화살표만 — 조회 범위 드롭다운은 본문 최상단 툴바로 내렸다.
          // 추이 데이터가 없으면 고를 범위 자체가 없으므로 툴바를 렌더하지 않는다(동작 불가 컨트롤 제거).
          toolbar={trendHasData && trendGroupOptions.length > 0 ? (
            <StatusToolbar label={t("trendScopeLabel")}>
              <StatusToolbarSelect
                value={trendGroup}
                onChange={setTrendGroup}
                options={[
                  { value: "", label: t("rpTrendGroupAll") },
                  ...trendGroupOptions.map(([cd, name]) => ({ value: cd, label: name })),
                ]}
              />
            </StatusToolbar>
          ) : undefined}
        >
          {trendHasData ? (
            <ForecastActualChart
              trend={selectedTrend}
              height={chartTrendHeight - trendToolbarH}
              colorMap={{ demand_qty: C.acc, rp_order_qty: C.g1,
                          eoh_qty: C.g2, safety_inv_qty: C.g3 }}
              dashKeys={["safety_inv_qty"]}
              emptyText={t("rpReplenishTrendEmpty")}
            />
          ) : (
            <StatusEmpty variant="chart" minHeight={chartTrendHeight}
                         text={t("rpReplenishTrendPendingDesc")} />
          )}
        </StatusSection>

        <StatusSection
          collapsible
          defaultOpen
          collapseDisabled={!loading && allIssueItems.length === 0}
          title={t("mainIssueTitle")}
          subtitle={t("mainIssueSubtitle")}
          right={!loading && allIssueItems.length === 0 ? <StatusPendingTag /> : undefined}
          cardSx={{ height: "100%" }}
          contentSx={{ p: "10px 15px !important" }}
        >
          {/* 이 Box 실측 높이로 좌측 차트 높이를 맞춘다(measureIssueBox → ResizeObserver) */}
          <Box ref={measureIssueBox}>
          {loading ? (
            <Stack spacing={0.7}>
              {/* 완료 후 1줄 행과 같은 높이로 맞춘다(로딩→완료 전환 시 높이 튐 방지) */}
              {[68, 52, 80].map((w, i) => (
                <Box key={i} sx={{ p: "6px 10px", borderRadius: 1,
                                   bgcolor: C.bg3, border: `0.5px solid ${C.border}`,
                                   display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: C.border, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ height: 10, width: `${w}%`, bgcolor: C.border, borderRadius: 1 }} />
                  </Box>
                  <Box sx={{ width: 36, height: 14, bgcolor: C.border, borderRadius: 1, flexShrink: 0 }} />
                </Box>
              ))}
            </Stack>
          ) : allIssueItems.length === 0 ? (
            // 스켈레톤 3행 — 릴리즈 후 이 자리에 무엇이 채워지는지 예고하고, 정상 상태와
            // 섹션 높이를 맞춰 좌측 차트가 짧아지지 않게 한다.
            <StatusEmpty variant="rows" count={3} text={t("MSG_NoStatusIssueData")} />
          ) : (
            // 헤더는 없지만 StatusItemList 로 감싼다 — 행마다 단위가 갈리므로 숫자 열·단위 열
            // 폭을 실측 동기화해야 단위 시작점이 세로로 정렬된다(열 라벨은 붙일 수 없는 리스트)
            <StatusItemList>
            <Stack spacing={0.7}>
              {visibleIssueItems.map((issue) => {
                const st = ISSUE_STYLE[issue.level];
                return (
                  <StatusItemRow key={issue.title}
                    accent={st.color}
                    bar={false}
                    name={(
                      <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: st.text,
                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {issue.title}
                      </Typography>
                    )}
                    sub={issue.desc}
                    // 숫자·단위를 두 열로 — 행마다 단위가 달라 한 셀에 붙이면 시작점이 튄다
                    cells={[
                      { w: ISSUE_CNT_W, unit: issue.unit ?? "SKU", num: (
                        <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: st.color, lineHeight: 1, whiteSpace: "nowrap" }}>
                          {issue.affectedCount}
                        </Typography>
                      ) },
                    ]}
                    rightW={ISSUE_KPI_CHIP_W}
                    right={(
                      <Chip label={issue.kpiLabel} size="small"
                        sx={{ bgcolor: st.bg, color: st.color,
                              border: `0.5px solid ${st.color}55`,
                              fontSize: FONT.CAPTION, height: 18, "& .MuiChip-label": { px: "5px" } }} />
                    )}
                  />
                );
              })}
            </Stack>
            </StatusItemList>
          )}
          </Box>
        </StatusSection>

      </Box>

      {/* ── 문제 항목 TOP 5 (통합 2열) ──────────────────────── */}
      <StatusSection
        collapsible
        defaultOpen
        collapseDisabled={topItemsPending}
        title={t("topProblemItemsTitle")}
        subtitle={t("rpTopProblemSubtitle")}
        right={topItemsPending ? <StatusPendingTag /> : undefined}
        contentSx={{ p: "10px 15px !important" }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5,
                       "& > *": { minWidth: 0 } }}>

          {/* 좌 칼럼: Short/Delay 발생 품목 TOP 5 (버전 BACKORDER 기준 단일 종류) */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
              <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>{t("rpSdOccurTop5Title", { sdLabel })}</Typography>
              <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("rpAggPeriodSubtitle", { kind: isDelay ? t("delayQtyLabel") : t("shortQtyLabel") })}</Typography>
            </Box>
            {loading ? (
              <Stack spacing={0.7}>
                {[55, 70, 45, 65, 50].map((w, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1,
                                     p: "6px 10px", borderRadius: 1,
                                     bgcolor: C.bg3, border: `0.5px solid ${C.border}` }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%",
                                   bgcolor: C.border, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ height: 9, width: `${w}%`, bgcolor: C.border, borderRadius: 1, mb: 0.5 }} />
                      <Box sx={{ height: 7, width: "40%", bgcolor: C.bg2, borderRadius: 1 }} />
                    </Box>
                    <Box sx={{ width: 36, height: 14, bgcolor: C.border, borderRadius: 1 }} />
                  </Box>
                ))}
              </Stack>
            ) : (!shortDelayItems || shortDelayItems.length === 0) ? (
              // 초록 체크(=문제 없음)를 쓰지 않는다 — 집계가 없는 구간은 판정 불가다.
              <StatusEmpty />
            ) : (
              <StatusItemList>
                <StatusItemHeader
                  nameLabel={`${t("item")} / ${t("location")}`}
                  rightW={SHORT_CHIP_W}
                  cells={[
                    { w: 48, label: t("fillRate"), unit: "%" },
                  ]} />
              <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
                {shortDelayItems.slice(0, 5).map((row, i) => {
                  const shortQty     = Number(row.short_qty     ?? 0);
                  const backorderQty = Number(row.backorder_qty ?? 0);
                  // 무수요 SKU(backorder-only)는 fill_rate_pct 가 NULL — 0%(최악)로 오인되지
                  // 않도록 null 을 유지해 "—" 로 표시한다.
                  const fillRateRow  = row.fill_rate_pct == null ? null : Number(row.fill_rate_pct);
                  // 미충족 수량: short_qty 우선, 없으면 backorder_qty 폴백
                  // (BACKORDER=allow 버전은 미충족분이 backorder_qty 로만 잡혀 short_qty=0 가능)
                  const unmetQty     = shortQty > 0 ? shortQty : backorderQty;
                  const shortLabel   = isDelay ? t("delayQtyLabel") : t("shortQtyLabel");
                  // 필요수량(demand) 대비 충족량(=필요−미충족) — %(충족률)의 분자·분모 노출
                  const demandQty    = row.demand_qty == null ? null : Number(row.demand_qty);
                  const filledQty    = demandQty == null ? null : Math.max(demandQty - unmetQty, 0);
                  // 충족률 단계색 — borderLeft·진행바·% 공용 (null=산출 불가 → 중립 회색)
                  const color = fillRateRow == null ? C.text3
                    : fillRateRow >= 95 ? C.g1 : fillRateRow >= 80 ? C.g2 : C.g3;
                  const locText = row.loc_name || row.loc_cd || "";
                  const itemText = row.item_name || row.item_cd || "—";
                  // Short/Delay 가 발생한 기간(버켓) 수 — fact.short_period_count
                  const periodCount = Number(row.period_count ?? 0);
                  return (
                    <StatusItemRow key={`${row.item_cd ?? "?"}-${row.loc_cd ?? i}`}
                      accent={color}
                      name={<NavName targets={[
                        { label: i18n.t("menuRpReview"), onClick: canDrillReview ? () => goToRpReview(row) : undefined },
                      ]}>{itemText}</NavName>}
                      sub={locText}
                      /* 충족량·부족량·필요량은 모두 바(충족률)에서 파생되는 값이라 행에 두면
                         같은 지표가 반복된다 — 절대 수치는 hover 툴팁으로 내리고 행에는
                         충족률 % 만 남긴다. 툴팁 순서는 바가 읽히는 순서(충족→부족→필요) */
                      bar={{ value: Math.min(Math.max(fillRateRow ?? 0, 0), 100), color,
                             ariaLabel: `${t("filledQtyLabel")} ${fmtQty(filledQty) ?? "—"} / `
                                      + `${shortLabel} ${fmtQty(unmetQty) ?? "—"} / `
                                      + `${t("requiredQtyLabel")} ${fmtQty(demandQty) ?? "—"}`,
                             tip: (
                               <BarTip title={`${itemText}${locText ? ` · ${locText}` : ""}`}
                                 rows={[
                                   { color, label: t("filledQtyLabel"), value: fmtQty(filledQty) ?? "—" },
                                   { color: C.g3, label: shortLabel,
                                     value: fmtQty(unmetQty) ?? "—", strong: true },
                                   {        label: t("requiredQtyLabel"), value: fmtQty(demandQty) ?? "—" },
                                 ]} />
                             ) }}
                      cells={[
                        { w: 48, unit: fillRateRow == null ? "" : "%", num: (
                          <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color, lineHeight: 1 }}>
                            {fillRateRow == null ? "—" : fillRateRow.toFixed(1)}
                          </Typography>
                        ) },
                      ]}
                      rightW={SHORT_CHIP_W}
                      // 칩은 "부족/지연이 몇 개월(주·일)치인가" — 버전 단위 Short/Delay 구분(모든 행
                      // 같은 값)을 대신해 행마다 갈리는 지속성(만성 vs 일시)을 노출한다.
                      // 3버켓 이상이면 위험색. 기간 수가 없으면 슬롯만 비우고 열은 유지한다
                      // (빼면 행끼리 지표 셀이 좌우로 밀림).
                      right={[
                        periodCount > 0 ? (
                          <Chip
                            label={t(bucketChipKey, { kind: isDelay ? t("delayed") : t("short"),
                                                      n: periodCount })}
                            size="small"
                            sx={{ bgcolor: periodCount >= 3 ? C.g3l : C.g2l,
                                  color:   periodCount >= 3 ? C.g3t : C.g2t,
                                  border:  `0.5px solid ${periodCount >= 3 ? C.g3s : C.g2s}`,
                                  fontSize: FONT.CAPTION, height: 19, "& .MuiChip-label": { px: "5px" }, flexShrink: 0 }}
                          />
                        ) : null,
                      ]} />
                  );
                })}
              </Stack>
              </StatusItemList>
            )}
          </Box>

          {/* 우 칼럼: 긴급 재고 부족 TOP 5 */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.8 }}>
              <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color: C.text1 }}>{t("rpUrgentShortageTitle")}</Typography>
              <Typography sx={{ fontSize: FONT.BODY, color: C.text3 }}>{t("rpUrgentShortageSubtitle")}</Typography>
            </Box>
            {loading ? (
              <Stack spacing={0.7}>
                {[55, 70, 45, 65, 50].map((w, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1,
                                     p: "6px 10px", borderRadius: 1,
                                     bgcolor: C.bg3, border: `0.5px solid ${C.border}` }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%",
                                   bgcolor: C.border, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ height: 9, width: `${w}%`, bgcolor: C.border, borderRadius: 1, mb: 0.5 }} />
                      <Box sx={{ height: 7, width: "40%", bgcolor: C.bg2, borderRadius: 1 }} />
                    </Box>
                    <Box sx={{ width: 36, height: 14, bgcolor: C.border, borderRadius: 1 }} />
                  </Box>
                ))}
              </Stack>
            ) : (
              <StatusItemList>
                {criticalItems && criticalItems.length > 0 && (
                  <StatusItemHeader
                    nameLabel={`${t("item")} / ${t("location")}`}
                    rightW={URGENT_CHIP_W}
                    cells={[
                      { w: 62, label: t("rpSafetyStockVsLabel"), unit: "%" },
                    ]} />
                )}
              <Stack spacing={0.7} sx={{ flex: 1, justifyContent: "flex-start" }}>
                {(criticalItems ?? []).map((row, i) => {
                  const safety    = Number(row.safety_inv_qty ?? 0);
                  const eoh       = Number(row.eoh_qty ?? 0);
                  const covDays   = Number(row.inv_coverage_days ?? 0);
                  // 리드타임 윈도(계획 시작~+LT: 발주로 방어불가 구간) 기준. 레거시 행
                  // (윈도 필드 없음)은 구 최종버켓 값으로 폴백해 기존과 동일하게 렌더.
                  const hasWindow = row.lt_window_days != null;
                  const shortfall = Number(row.lt_window_shortfall_qty ?? (safety - eoh));
                  const minEoh    = Number(row.lt_window_min_eoh_qty ?? eoh);
                  const ltDays    = Number(row.lt_window_days ?? 0);
                  const isNegative = row.is_negative_eoh === true || minEoh < 0;
                  // 안전재고 대비 윈도 내 최저재고 충족률(진행바·%)
                  const invPct    = safety > 0 ? Math.min(Math.max((minEoh / safety) * 100, 0), 100) : 0;
                  const color   = isNegative ? C.g3 : C.g2;
                  const locText = row.loc_name || row.loc_cd || "";
                  const itemText = row.item_name || row.item_cd || "—";
                  return (
                    <StatusItemRow key={i}
                      accent={color}
                      name={<NavName targets={[
                        { label: i18n.t("menuRpReview"), onClick: canDrillReview ? () => goToRpReview(row) : undefined },
                      ]}>{itemText}</NavName>}
                      sub={locText}
                      /* 최저재고·부족량·안전재고는 모두 바(안전재고 대비 %)에서 파생되는 값이라
                         행에서 빼고 hover 툴팁으로 내린다 — 행에 남는 숫자는 % 뿐.
                         툴팁 순서는 바가 읽히는 순서(최저재고→부족→안전재고) */
                      bar={{ value: invPct, color,
                             ariaLabel: `${t("rpLtWindowMinInvLabel")} ${fmtQty(minEoh) ?? "—"} / `
                                      + `${t("shortQtyLabel")} ${fmtQty(shortfall) ?? "—"} / `
                                      + `${t("safetyStockLabel")} ${fmtQty(safety) ?? "—"}`,
                             tip: (
                               <BarTip title={`${itemText}${locText ? ` · ${locText}` : ""}`}
                                 rows={[
                                   { color, label: t("rpLtWindowMinInvLabel"), value: fmtQty(minEoh) ?? "—" },
                                   { color: C.g3, label: t("shortQtyLabel"),
                                     value: fmtQty(shortfall) ?? "—", strong: true },
                                   {        label: t("safetyStockLabel"),      value: fmtQty(safety) ?? "—" },
                                 ]} />
                             ) }}
                      cells={[
                        { w: 62, unit: "%", num: (
                          <Typography sx={{ fontSize: FONT.SUB, fontWeight: 700, color, lineHeight: 1 }}>{invPct.toFixed(0)}</Typography>
                        ) },
                      ]}
                      rightW={URGENT_CHIP_W}
                      // 슬롯 고정 — 결품 칩이 없는 행도 그 자리를 지켜 LT 칩이 밀리지 않는다
                      right={[
                        isNegative ? (
                          <Chip
                            label={t("rpStockoutBadge")}
                            size="small"
                            sx={{ bgcolor: C.g3l, color: C.g3t, border: `0.5px solid ${C.g3s}`,
                                  fontSize: FONT.CAPTION, height: 19, "& .MuiChip-label": { px: "5px" }, flexShrink: 0 }}
                          />
                        ) : null,
                        <Chip
                          label={hasWindow
                            ? t("rpLtWindowChip", { days: ltDays.toFixed(0) })
                            : t("rpCoverDaysChip", { days: covDays.toFixed(0) })}
                          size="small"
                          sx={{ bgcolor: isNegative ? C.g3l : C.g2l,
                                color:   isNegative ? C.g3t : C.g2t,
                                border:  `0.5px solid ${isNegative ? C.g3s : C.g2s}`,
                                fontSize: FONT.CAPTION, height: 19, "& .MuiChip-label": { px: "5px" }, flexShrink: 0 }}
                        />,
                      ]} />
                  );
                })}
                {/* 빈 상태는 좌측 목록과 동일한 공용 컴포넌트로 통일 — 초록 체크(✓)·앰버 박스 폐기.
                    "미조회(null)"와 "0건"을 문구로만 구분한다(둘 다 중립 표현). */}
                {(!criticalItems || criticalItems.length === 0) && (
                  <StatusEmpty text={criticalItems ? undefined : t("noDataFoundLabel")} />
                )}
              </Stack>
              </StatusItemList>
            )}
          </Box>

        </Box>
      </StatusSection>

    </Stack>
  );
}

// ── RP 모듈 설정 (AiScenarioPlanning 이 import 하는 실제 데이터) ──────────
// ── 공급 네트워크 구조 그래프 (RP 시나리오 구성 탭 — 파라미터·엔진 sub-step) ──
// 공통 화면(StepParamEngine)이 mod.ComposeExtraContent 로 주입받아 렌더한다.
// 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처(SupplyNetworkMap)에서 t()로 변환한다.
const LOC_TYPE_LABEL = {
  VENDOR: "rpLocTypeVendor", SUPPLIER: "rpLocTypeVendor", MANUFACTURER: "rpLocTypeManufacturer",
  CDC: "rpLocTypeCdc", IDC: "rpLocTypeIdc", RDC: "rpLocTypeRdc",
  DC: "rpLocTypeIdc", WH: "whse", WAREHOUSE: "whse",
  STORE: "rpLocTypeStore", BRANCH: "rpLocTypeBranch", SALES: "rpLocTypeSales", RETAIL: "rpLocTypeRetail",
};
// 레벨별 색상 스타일 — 3단계 팔레트
const LEVEL_STYLE = [
  { bg: C.accL,    border: C.accS,    labelColor: C.accT  },  // Lv0 공급사
  { bg: "#E1F5EE", border: "#5DCAA5", labelColor: C.g1t   },  // Lv1 DC
  { bg: C.bg2,     border: C.border,  labelColor: C.text1 },  // Lv2 판매채널
  { bg: C.g2l,     border: C.g2s,     labelColor: C.g2t   },  // Lv3+
];

// 데이터: rpScenarioAiService.getNetwork() 실데이터 (BFS 레벨 감지)
function SupplyNetworkMap({ edges, loading }) {
  const { t } = useTranslation();
  // 접기/펼치기 — 기본 펼침. 로딩·빈 상태에서는 토글 미노출(본문 없음).
  const [open, setOpen] = useState(true);

  // ── 로딩 상태 ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 0, bgcolor: "transparent", border: `0.5px solid ${C.border}`,
                                borderRadius: 2, overflow: "hidden", mb: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 1.5, py: 1, bgcolor: C.bg3 }}>
          <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>
            {t("rpNetworkStructureTitle")}
          </Typography>
          <CircularProgress size={12} sx={{ color: C.acc, ml: 0.5 }} />
        </Stack>
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, px: 1.5, py: 1.2 }}>{t("rpNetworkLoadingLabel")}</Typography>
      </Paper>
    );
  }

  // ── 빈 데이터 상태 ─────────────────────────────────────────────────────
  if (!Array.isArray(edges) || edges.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 0, bgcolor: "transparent", border: `0.5px solid ${C.border}`,
                                borderRadius: 2, overflow: "hidden", mb: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 1.5, py: 1, bgcolor: C.bg3 }}>
          <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>
            {t("rpNetworkStructureTitle")}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, px: 1.5, py: 1.2 }}>
          {t("rpNetworkNoDataLabel")}
        </Typography>
      </Paper>
    );
  }

  // ── 1. 노드 수집 ──────────────────────────────────────────────────────
  const nodeMap = {};
  edges.forEach(e => {
    if (!nodeMap[e.origin_loc_cd])
      nodeMap[e.origin_loc_cd] = {
        cd: e.origin_loc_cd,
        name: e.origin_loc_name || e.origin_loc_cd,
        type: (e.origin_loc_type || "").toUpperCase(),
      };
    if (!nodeMap[e.destination_loc_cd])
      nodeMap[e.destination_loc_cd] = {
        cd: e.destination_loc_cd,
        name: e.destination_loc_name || e.destination_loc_cd,
        type: (e.destination_loc_type || "").toUpperCase(),
      };
  });

  // ── 2. 에지 중복 제거 (같은 FROM-TO는 LT 평균) ──────────────────────
  const edgeAcc = {};
  edges.forEach(e => {
    const k = `${e.origin_loc_cd}__${e.destination_loc_cd}`;
    if (!edgeAcc[k]) edgeAcc[k] = { from: e.origin_loc_cd, to: e.destination_loc_cd, lts: [] };
    if (e.lt_days != null) edgeAcc[k].lts.push(Number(e.lt_days));
  });
  const uniqEdges = Object.values(edgeAcc).map(e => ({
    from: e.from, to: e.to,
    lt: e.lts.length ? Math.round(e.lts.reduce((a, b) => a + b, 0) / e.lts.length) : null,
  }));

  // ── 3. BFS로 레벨 감지 ─────────────────────────────────────────────────
  const allCds      = Object.keys(nodeMap);
  const incomingSet = new Set(uniqEdges.map(e => e.to));
  const adjMap      = {};
  uniqEdges.forEach(e => {
    if (!adjMap[e.from]) adjMap[e.from] = [];
    adjMap[e.from].push(e.to);
  });
  const levelOf = {};
  const bfsQ    = allCds.filter(cd => !incomingSet.has(cd));
  bfsQ.forEach(cd => { levelOf[cd] = 0; });
  let qi = 0;
  while (qi < bfsQ.length) {
    const cur = bfsQ[qi++];
    (adjMap[cur] || []).forEach(next => {
      if (levelOf[next] === undefined) { levelOf[next] = levelOf[cur] + 1; bfsQ.push(next); }
    });
  }
  allCds.forEach(cd => { if (levelOf[cd] === undefined) levelOf[cd] = 1; });

  // ── 4. NODE_GROUPS 구성 ─────────────────────────────────────────────
  const levelGroups = {};
  allCds.forEach(cd => {
    const lv = levelOf[cd];
    if (!levelGroups[lv]) levelGroups[lv] = [];
    levelGroups[lv].push(nodeMap[cd]);
  });
  const sortedLevels = Object.keys(levelGroups).map(Number).sort();
  const NODE_GROUPS = sortedLevels.map((lv, li) => {
    const style = LEVEL_STYLE[li] || LEVEL_STYLE[LEVEL_STYLE.length - 1];
    return {
      ...style,
      nodes: levelGroups[lv].map(n => ({
        name: n.name || n.cd,
        sub:  LOC_TYPE_LABEL[n.type] ? t(LOC_TYPE_LABEL[n.type]) : (n.type || t("location")),
      })),
    };
  });

  // ── 5. ARROWS 구성 (인접 레벨 간 LT 평균 텍스트) ────────────────────
  //   범위(min~max) 대신 평균치를 표시한다. LT 원천은 백엔드 fetch_supply_network 에서
  //   BOD ITEM(품목별) LT 를 경로별 평균낸 값이다.
  const ARROWS = sortedLevels.slice(0, -1).map(lv => {
    const nextLv   = lv + 1;
    const fromCds  = (levelGroups[lv]       || []).map(n => n.cd);
    const toCds    = (levelGroups[nextLv]    || []).map(n => n.cd);
    const lts = uniqEdges
      .filter(e => fromCds.includes(e.from) && toCds.includes(e.to) && e.lt != null)
      .map(e => e.lt);
    if (!lts.length) return t("rpConnectedLabel");
    const avgLt = Math.round(lts.reduce((a, b) => a + b, 0) / lts.length);
    return `LT ${avgLt}${t("dayUnit")}`;
  });

  // ── 6. 레전드 데이터 (실제 레벨 기준으로 동적 생성) ─────────────────
  const LEGEND = NODE_GROUPS.map((grp, li) => ({
    label: grp.nodes[0]?.sub || `Level ${li}`,
    bg: grp.bg, border: grp.border, color: grp.labelColor,
  }));

  return (
    <Paper elevation={0} sx={{ p: 0, bgcolor: "transparent", border: `0.5px solid ${C.border}`,
                              borderRadius: 2, overflow: "hidden", mb: 0 }}>
      {/* 헤더 (클릭 시 접기/펼치기) — 헤더에만 배경색을 주어 클릭 가능함을 표시, 본문은 배경 없음 */}
      <Stack direction="row" alignItems="center" gap={0.5}
             onClick={() => setOpen(o => !o)}
             sx={{ px: 1.5, py: 1, bgcolor: C.bg3, cursor: "pointer", userSelect: "none" }}>
        <Typography sx={{ fontSize: FONT.TITLE, fontWeight: 700, color: C.text1 }}>
          {t("rpNetworkStructureTitle")}
        </Typography>
        <IconButton size="small" sx={{ ml: "auto", p: 0.3, color: C.text3 }}
                    onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>
          <ExpandMoreIcon sx={{ fontSize: 18, transform: open ? "rotate(180deg)" : "none",
                                transition: "transform .15s" }} />
        </IconButton>
      </Stack>

      {/* 본문 (펼침 상태에서만) — 배경 없음. 레전드는 본문 우측 상단에 오버레이(높이 미증가). */}
      {open && (
      <Box sx={{ position: "relative", px: 1.5, py: 1.5 }}>
      {/* 레전드 — 우측 상단 오버레이. 노드와 겹쳐도 읽히도록 배경 부여. */}
      <Stack direction="row" gap={1.2} flexWrap="wrap"
             sx={{ position: "absolute", top: 6, right: 12, zIndex: 2,
                   bgcolor: C.bg2, borderRadius: 1, px: 0.8, py: 0.4,
                   border: `0.5px solid ${C.border}` }}>
        {LEGEND.map(l => (
          <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: 0.5,
                       bgcolor: l.bg, border: `0.5px solid ${l.border}` }} />
            <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>{l.label}</Typography>
          </Box>
        ))}
      </Stack>
      {/* 노드 플로우 */}
      <Box sx={{ display: "flex", alignItems: "center", overflowX: "auto", gap: 0 }}>
        {NODE_GROUPS.map((grp, gi) => (
          <Box key={gi} sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            {/* 노드 그룹 — 동일 레벨 카드 너비 통일: alignItems:stretch 가 컬럼 교차축(가로)을
                최장 카드 너비로 늘려 모두 동일 폭. width:100% 는 shrink-to-fit 컬럼에서 넘침을
                유발하므로 쓰지 않는다. */}
            <Stack spacing={1} alignItems="stretch" sx={{ flexShrink: 0 }}>
              {grp.nodes.map((nd, ni) => (
                <Box key={ni}
                  sx={{ borderRadius: 1.5, px: 1.5, py: "7px", textAlign: "center",
                         border: `0.5px solid ${grp.border}`, bgcolor: grp.bg,
                         minWidth: 100 }}>
                  <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: grp.labelColor }}>
                    {nd.name}
                  </Typography>
                  <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, mt: 0.3 }}>
                    {nd.sub}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* 화살표 (마지막 그룹 제외) */}
            {gi < NODE_GROUPS.length - 1 && (
              <Stack alignItems="center" spacing={0.4} sx={{ px: 2, flexShrink: 0 }}>
                <Box sx={{ position: "relative", width: 36, height: 16,
                           display: "flex", alignItems: "center" }}>
                  <Box sx={{ flex: 1, height: 1.5, bgcolor: C.border }} />
                  <Box sx={{ width: 0, height: 0,
                             borderTop: "4px solid transparent",
                             borderBottom: "4px solid transparent",
                             borderLeft: `6px solid ${C.border}` }} />
                </Box>
                <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, whiteSpace: "nowrap" }}>
                  {ARROWS[gi]}
                </Typography>
              </Stack>
            )}
          </Box>
        ))}
      </Box>
      </Box>
      )}
    </Paper>
  );
}

// 공통 StepParamEngine 이 주입받아 렌더하는 RP 전용 시각화 (네트워크 조회·캐싱 자체 처리)
function RpComposeExtraContent({ cache, onCacheChange }) {
  const [edges,   setEdges]   = useState(Array.isArray(cache) ? cache : []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (Array.isArray(cache) && cache.length > 0) return; // 배열 캐시 있으면 재조회 생략(narrative 객체 폴백은 무시)
    setLoading(true);
    rpScenarioAiService.getNetwork()
      .then(res => {
        const e = res.data?.edges || [];
        setEdges(e);
        onCacheChange?.(e); // 부모(agentResults)에 캐시 전달
      })
      .catch(err => console.warn("[RP] 공급 네트워크 조회 실패:", err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <SupplyNetworkMap edges={edges} loading={loading} />;
}

// ── RP 시나리오 파라미터 위젯 (EditableScenarioCards 가 mod.CustomScenarioFields 로 주입받아 렌더) ──
// 2가지 경로(직접 입력 시나리오도 paramDefsUnion 으로 param_defs 합집합을 주입받아 1) 경로로 편집):
//  1) DB 표준/직접 입력 시나리오 + overrides — 변경 대상(거점/품목군) 행 목록 편집 (AI 제안 + 사용자 추가/삭제)
//  2) DB 표준 시나리오 base — "현행 유지" 안내 (변경 없음 기준선)
// label 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처(RpScenarioParamFields)에서 t()로 변환한다.
//
// 적용 단위(scope) 메타 — 신규 작성은 loc/item_grp/all 만 노출(품목→품목군 전환).
// item 은 과거 저장된 시나리오(scope_type:"item")를 계속 표시·편집하기 위한 레거시 전용 항목
// (토글에는 복원된 값이 "item" 일 때만 조건부로 노출되고, 다른 단위로 전환하면 다시 사라짐).
const RP_SCOPE_META = {
  loc:      { cdKey: "loc_cd",      nameKey: "loc_name",      candKey: "locations",   label: "location",           placeholderKey: "rpSelectLocationPlaceholder" },
  item_grp: { cdKey: "item_grp_cd", nameKey: "item_grp_name", candKey: "item_groups", label: "scopeItemGrpLabel",  placeholderKey: "rpSelectItemGrpPlaceholder" },
  item:     { cdKey: "item_cd",     nameKey: "item_name",     candKey: "items",       label: "item",               placeholderKey: "rpSelectItemPlaceholder" }, // 레거시 표시 전용
};
const OV_SCOPE_OPTIONS = [
  { value: "loc",      label: "location" },
  { value: "item_grp", label: "scopeItemGrpLabel" },
  { value: "all",      label: "all" },
];

function RpScenarioParamFields({ scenario, accent, disabled, highlights, onParamChange, onScenarioChange,
                                 view = "all", onStats }) {
  const { t } = useTranslation();

  // 기준선 현재값 표시 — baseline_kind 접두어(현행/평균) + 현재값. 파라미터명은 왼쪽 라벨 열이 이미 말하므로
  //   재출력하지 않는다. custom(캘린더 적용 비율)은 이름 재출력이 아니라 별도 지표라 desc 전문을 유지한다.
  const baselineValue = (def) => {
    const cv = def?.current_value;
    if (!def?.baseline_desc_key || cv == null || cv === "") return null;
    if (def.type === "select") {
      const opt = (def.options || []).find(o => String(o.value) === String(cv));
      return opt ? t(opt.label_key || opt.label) : String(cv);
    }
    const unit = def.unit_key ? t(def.unit_key) : (def.unit || "");
    return `${Number.isFinite(Number(cv)) ? Number(cv) : cv}${unit}`;
  };
  const fmtBaselineCurrent = (def) => {
    const valPart = baselineValue(def);
    if (valPart == null) return def?.current_display || null;
    return baselineCurrentText(def, valPart, t);
  };
  // base 카드는 헤더가 이미 '현행 유지'라고 말한다 — 행마다 '현행'을 되풀이하지 않고 값만. '평균'은 값의 성격이라 남긴다.
  const fmtBaseCardCurrent = (def) => {
    const valPart = baselineValue(def);
    if (valPart == null) return def?.current_display || null;
    if (def.baseline_kind === "avg") return t("paramAvgValueLabel", { value: valPart });
    if (def.baseline_kind === "current") return valPart;
    return baselineCurrentText(def, valPart, t);   // custom·구버전 응답
  };

  const inputSx = {
    width: "100%", p: "4px 8px", border: `0.5px solid ${accent}60`,
    borderRadius: 1, fontSize: FONT.BODY, fontFamily: "inherit", color: C.text1,
    bgcolor: C.bg2, outline: "none", boxSizing: "border-box",
    "&:focus": { borderColor: accent },
    "&:disabled": { bgcolor: C.bg3, color: C.text3, cursor: "not-allowed", borderColor: C.border },
  };

  // AI Agent set_param 변경 강조 (공통 제너릭 경로와 동일 키 규칙: `${role}:${paramKey}`)
  const hotWrap = (paramKey, node) => {
    const hlTs = highlights?.[`${scenario.role}:${paramKey}`];
    if (hlTs == null) return <Box key={paramKey}>{node}</Box>;
    return (
      <Box key={paramKey}
        sx={{ position: "relative", borderRadius: PARAM_ROW_RADIUS, p: "8px 9px", mx: "-9px",
              bgcolor: `${C.acc}10`, border: `1px solid ${C.acc}55`,
              boxShadow: `0 0 0 3px ${C.acc}12` }}>
        <Box sx={{ position: "absolute", top: -7, right: 7, px: "5px", height: 14,
                   borderRadius: PARAM_ROW_RADIUS, bgcolor: C.acc, color: "#fff", fontSize: FONT.MICRO,
                   fontWeight: 700, display: "flex", alignItems: "center" }}>{t("aiChangedBadge")}</Box>
        {node}
      </Box>
    );
  };

  // 파라미터 입력 1개 렌더 — 전역 params / override.params 공용 (value·onChange 주입)
  // currentText: 입력 옆 참고 현재값(거점/품목별 실제값). 미지정 시 showCurrent + 전역 current_display.
  const renderDef = (def, value, onChange, showCurrent = true, currentText = undefined, hideLabel = false) => {
    const curLine = currentText !== undefined ? currentText : (showCurrent ? fmtBaselineCurrent(def) : null);
    return (
    <Box key={def.param_key}>
      {!hideLabel && (
        <Typography sx={{ fontSize: FONT.BODY, color: C.text3, mb: 0.3 }}>{t(def.label_key || def.label)}</Typography>
      )}
      {def.type === "select" ? (
        <Box component="select" value={value ?? def.options?.[0]?.value ?? ""} disabled={disabled}
          onChange={e => onChange(def.param_key, e.target.value)}
          sx={{ ...inputSx, ...PARAM_SELECT_W, cursor: disabled ? "not-allowed" : "pointer" }}>
          {(def.options || []).map(o => <option key={o.value} value={o.value}>{t(o.label_key || o.label)}</option>)}
        </Box>
      ) : def.type === "number" ? (
        /* 절대값 입력 — 단위(일/EA) 접미 표시 */
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box component="input" type="number" value={value ?? ""} disabled={disabled} placeholder="0"
            onChange={e => onChange(def.param_key, e.target.value)}
            sx={{ ...inputSx, width: PARAM_NUM_INPUT_W, flex: "0 0 auto" }} />
          {def.unit && (
            <Typography sx={{ fontSize: FONT.BODY, color: C.text3, flex: "0 0 auto" }}>{def.unit_key ? t(def.unit_key) : def.unit}</Typography>
          )}
        </Box>
      ) : def.type === "toggle" ? (
        /* 2택 세그먼트 — 체크박스보다 표적이 크고 현재 선택이 한눈에 보인다(IP 와 동일 규격). */
        <AiSegmentedControl accent={accent} disabled={disabled}
          value={String(value).toUpperCase() === "Y" ? "Y" : "N"}
          options={[{ value: "N", label: t("rpCalendarKeepShort") },
                    { value: "Y", label: t("rpCalendarOffShort") }]}
          onChange={(v) => onChange(def.param_key, v)} sx={PARAM_TOGGLE_SX} />
      ) : def.type === "multiplier" ? (
        /* 배수 입력 — "배" 접미 (예: 1.2배 = 현재값의 1.2배) */
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box component="input" type="number" step="0.1" min="0" value={value ?? ""} disabled={disabled}
            placeholder="1.0" onChange={e => onChange(def.param_key, e.target.value)}
            sx={{ ...inputSx, width: PARAM_NUM_INPUT_W, flex: "0 0 auto" }} />
          <Typography sx={{ fontSize: FONT.BODY, color: C.text3, flex: "0 0 auto" }}>{t("multiplierUnitLabel")}</Typography>
        </Box>
      ) : (
        /* percent — "90%" 형식 텍스트 입력 (레거시) */
        <Box component="input" value={value ?? ""} disabled={disabled} placeholder="100%"
          onChange={e => onChange(def.param_key, e.target.value)} sx={inputSx} />
      )}
      {curLine && (
        <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3, mt: 0.3 }}>
          {t("current")}: {curLine}
        </Typography>
      )}
    </Box>
    );
  };

  // ── 파라미터별 접기/펼치기(아코디언) ──────────────────────────────────────
  // **단일 펼침** — 편집 중인 파라미터 하나만 펼친다. 조정 여부는 접힌 행의 "현행 A → B" 가 알린다.
  const [openUid, setOpenUid] = useState(null);
  // 조정만 보기에서 "파라미터 추가"로 끌어온 행 — 값이 없어 조정 판정에 안 걸리므로 고정한다.
  const [pinnedUids, setPinnedUids] = useState(() => new Set());
  useEffect(() => {
    setOpenUid(null);
    setPinnedUids(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.role, scenario?.scenario_cd]);
  // 편집을 blur 로 끝낸 직후의 클릭은 무시 — 안 그러면 같은 행을 눌러 끝냈는데 도로 열린다.
  const editGuard = useEditEndGuard();
  const endEdit = () => { editGuard.markEnd(); setOpenUid(null); };
  const toggleOpen = (uid) => {
    if (editGuard.justEnded()) return;
    setOpenUid(cur => (cur === uid ? null : uid));
  };

  // ── 조정 건수 보고 + 뷰 필터 (공용 ScenarioParamDefsFields 와 동일 계약) ────────
  const _statKeys = new Set();
  for (const o of (Array.isArray(scenario?.overrides) ? scenario.overrides : [])) {
    for (const [k, v] of Object.entries(o?.params || {})) {
      if (hasEffectiveValue(null, v)) _statKeys.add(k);
    }
  }
  const _changedN = _statKeys.size;
  const _totalN   = scenario?.param_defs?.length || 0;
  useEffect(() => {
    if (scenario?.role === "base") return;
    onStats?.({ changed: _changedN, total: _totalN });
    // onStats 는 부모가 매 렌더 새로 만드는 함수라 deps 에서 제외(넣으면 무한 루프).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_changedN, _totalN, scenario?.role]);

  const isVisible = (uid, applied) => view === "all" || applied || pinnedUids.has(uid);
  const pinAndOpen = (uid) => {
    setPinnedUids(s => { const n = new Set(s); n.add(uid); return n; });
    setOpenUid(uid);
  };
  const addCandidates = (defs, uidOf, appliedOf) => defs
    .filter(d => !isVisible(uidOf(d), appliedOf(d)))
    .map(d => ({ key: uidOf(d), label: t(d.label_key || d.label) }));

  // 컴팩트 헤더 아코디언 1개 — 헤더(라벨·"현행 A → B"·되돌리기·▾) + 펼침 시 본문 입력.
  const accordionParam = (uid, def, value, onChange, currentText, last = false) => {
    const hlTs = highlights?.[`${scenario.role}:${def.param_key}`];
    const hot = hlTs != null;
    // hot 은 **강조 전용** — 펼침(편집) 상태에 OR 하지 않는다(하이라이트 해제 코드가 없어
    //   blur 로도 입력창이 안 닫히고, 값 셀 포커스가 Agent Chat 입력창을 뺏는다).
    const open = openUid === uid;
    const applied = hasEffectiveValue(def, value);
    const toggleText = (v) =>
      t(String(v).toUpperCase() === "Y" ? "rpCalendarOffShort" : "rpCalendarKeepShort");
    const afterText = fmtParamValue(def, value, t, toggleText);
    // 단일 컨트롤 타입은 행 클릭 = 값 셀에서 바로 편집(펼침 본문 없음).
    const inline = isInlineEditable(def);
    const inlineEditing = inline && open && !disabled;
    return (
      // key 에 hlTs 를 섞지 않는다 — hot 스타일에 애니메이션이 없어 remount 는 시각 효과가 0이고,
      // 같은 파라미터 재변경 때 편집 effect 만 재실행시킨다.
      <Box key={uid}
        sx={paramRowSx({ accent, applied, hot, last })}>
        <Box onClick={() => !disabled && toggleOpen(uid)}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, px: PARAM_ROW_PAD_X, py: "6px",
                height: PARAM_ROW_MIN_H, boxSizing: "border-box", overflow: "hidden",
                cursor: disabled ? "default" : "pointer", userSelect: "none",
                "&:hover": disabled ? {} : { bgcolor: `${accent}08` } }}>
          {/* 라벨은 폭을 고정한다 — grow 를 주면 편집 중 값 셀과 남는 공간을 나눠 가져 입력이 밀린다. */}
          <Typography noWrap sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text1,
                                   flex: "0 0 36%", minWidth: 0 }}>
            {t(def.label_key || def.label)}
          </Typography>
          <ParamValueCells applied={applied ? afterText : null}
            current={currentText} currentPrefix={currentPrefixFor(def, applied, t)}
            accent={hot ? C.acc : accent}
            input={inlineEditing ? renderDef(def, value, onChange, false, null, true) : null}
            onEditEnd={endEdit} />
          {hot && (
            <Box sx={{ flex: "0 0 auto", height: 15, px: "6px", borderRadius: "999px",
                       bgcolor: C.acc, color: "#fff", fontSize: FONT.MICRO, fontWeight: 700,
                       display: "flex", alignItems: "center" }}>{t("aiChangedBadge")}</Box>
          )}
          {/* 값이 있으면 "되돌리기", 값 없이 추가만 한 행이면 "목록에서 빼기". */}
          {!disabled && (
            <ParamRevertButton accent={accent} hidden={!applied && !pinnedUids.has(uid)}
              title={t(applied ? "rpRevertToCurrentLabel" : "removeParamLabel")}
              onClick={() => {
                onChange(def.param_key, "");
                // 추가(pin)해서 끌어온 행이면 고정도 함께 푼다(값만 비고 행이 남지 않도록).
                setPinnedUids(s => { const n = new Set(s); n.delete(uid); return n; });
                setOpenUid(cur => (cur === uid ? null : cur));
              }} />
          )}
          {/* 펼침 표시는 본문이 따로 있는 행에만. 인라인 행에서도 자리는 남긴다(열 정렬 유지). */}
          <Box component="span" sx={{ flex: "0 0 auto", color: C.text3, fontSize: 11, lineHeight: 1,
                width: 11, textAlign: "center",
                ...(inline ? { visibility: "hidden" } : {}),
                transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</Box>
        </Box>
        {open && !inline && (
          <Box sx={{ px: PARAM_ROW_PAD_X, pb: "8px", pt: "4px", borderTop: `0.5px dashed ${C.border}` }}>
            {renderDef(def, value, onChange, false, undefined, true)}
          </Box>
        )}
      </Box>
    );
  };

  // ── 1)·2) DB 표준 시나리오 (param_defs 존재) ──────────────────────────────
  if (scenario?.param_defs?.length) {
    const overrides = Array.isArray(scenario.overrides) ? scenario.overrides : null;

    // 구버전 응답(overrides 미존재) 폴백 — 전역 params 직접 편집
    if (overrides === null) {
      return (
        <Stack spacing={0.8}>
          {scenario.param_defs.map(def =>
            hotWrap(def.param_key,
              renderDef(def, scenario.params?.[def.param_key], onParamChange)))}
        </Stack>
      );
    }

    // base — 무변경 기준선
    if (scenario.role === "base") {
      return (
        <Box sx={{ p: "10px 10px", borderRadius: 1, bgcolor: C.bg3,
                   border: `0.5px dashed ${C.border}` }}>
          <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: C.text2, mb: 0.4 }}>
            {t("rpKeepCurrentTitle")}
          </Typography>
          {scenario.param_defs.filter(d => d.current_display).map(d => (
            <Typography key={d.param_key} sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
              {t(d.label_key || d.label)} · {fmtBaseCardCurrent(d)}
            </Typography>
          ))}
        </Box>
      );
    }

    // 변경 대상 후보 (fetchTemplates 가 variant 에 주입한 problem_targets) — 거점/품목군(레거시: 품목) 단위.
    // RP_SCOPE_META 기반으로 일반화 — 신규 scope(item_grp) 추가 시 이 헬퍼들만 보면 됨(이진 분기 없음).
    const targets = scenario.problem_targets || {};
    const optionsFor = (mode) => {
      const meta = RP_SCOPE_META[mode];
      if (!meta) return [];
      return (targets[meta.candKey] || [])
        .map(t => ({ cd: t[meta.cdKey], name: t[meta.nameKey], reason: t.reason,
                     reason_i18n: t.reason_i18n })).filter(o => o.cd);
    };
    // 거점/품목군별 현재 파라미터값 맵 (참고 현재값) — param_key 기준
    const currentFor = (mode) => {
      const meta = RP_SCOPE_META[mode];
      if (!meta) return {};
      return Object.fromEntries((targets[meta.candKey] || []).map(t => [t[meta.cdKey], t.current || {}]));
    };
    // 거점/품목군별 근거(SHORT 수량·충족률) 맵 — 대상 변경 시 새 거점/품목군 근거 재조회용
    const reasonFor = (mode) => Object.fromEntries(optionsFor(mode).map(o => [o.cd, o.reason ?? null]));
    // 같은 근거의 병행 i18n 파트([{key,args}]) 맵 — 표시는 화면 언어로 조립한다(ko 원문은 폴백).
    const reasonI18nFor = (mode) =>
      Object.fromEntries(optionsFor(mode).map(o => [o.cd, o.reason_i18n ?? null]));
    const reasonTextOf = (mode, cd, koFallback) => {
      const parts = reasonI18nFor(mode)[cd];
      return (Array.isArray(parts) && parts.length)
        ? parts.map(p => cellText(t, p, "")).filter(Boolean).join(" · ")
        : (koFallback || "");
    };

    // 현재값 → 참고 표시 문자열 (number=값+단위, select=정책 라벨, toggle=적용%)
    //  - cur 미선택/데이터 없음(undefined) → 전역 평균 폴백
    //  - cur 있으나 해당 파라미터 값 없음 → 그 거점/품목에 정보 없음 → 0 표시
    const fmtCurrent = (def, cur) => {
      if (cur == null) return fmtBaselineCurrent(def);       // 미선택 → 전역 평균
      const v = cur[def.param_key];
      if (def.type === "select") {
        if (!v) return t("noInfoLabel");
        const opt = (def.options || []).find(o => o.value === v);
        return opt ? t(opt.label_key || opt.label) : String(v);
      }
      if (def.type === "toggle") return t("rpAppliedPctLabel", { pct: v ?? 0 });
      return `${v ?? 0}${def.unit_key ? t(def.unit_key) : (def.unit || "")}`;   // 값 없으면 0 (해당 거점/품목 MOQ 등 미설정)
    };

    // 시나리오 단위 단일 scope — 행 전체가 동일 scope_type 보장(첫 행 기준).
    // 빈 배열(직접 입력 시나리오 초기 상태)은 "전체" 기본 — 아무 대상도 미선택 상태에서 시작.
    const scopeMode = overrides[0]?.scope_type ?? "all";

    // 신규 행/스코프 전환 시 시작 파라미터값 — 기존 overrides params 가 있으면 그대로 이어받고,
    // 없으면 빈 객체(아무 값도 변경되지 않은 상태로 시작). 직접 입력 시나리오는 항상 빈 값에서 출발.
    const presetParams = () => (overrides[0]?.params ? { ...overrides[0].params } : {});

    const setOverrides = (next) => onScenarioChange?.(sc => ({ ...sc, overrides: next }));
    const patchOv  = (i, patch) =>
      setOverrides(overrides.map((o, j) => (j === i ? { ...o, ...patch } : o)));
    const patchOvParam = (i, key, val) =>
      setOverrides(overrides.map((o, j) =>
        (j === i ? { ...o, params: { ...o.params, [key]: val } } : o)));
    const removeOv = (i) => setOverrides(overrides.filter((_, j) => j !== i));

    // 한 override 행을 scope 메타 기준으로 구성 — 신규 shape(loc_cd/item_cd/item_grp_cd 전 필드 보유,
    // 해당 scope 의 cdKey/nameKey 만 채우고 나머지는 null) 준수.
    const mkRow = (mode, cd, name, reason, params) => {
      const m = RP_SCOPE_META[mode];
      const row = { scope_type: mode, loc_cd: null, item_cd: null, item_grp_cd: null,
                    loc_name: null, item_name: null, item_grp_name: null,
                    params, reason: reason ?? null };
      if (m) { row[m.cdKey] = cd ?? ""; row[m.nameKey] = name; }
      return row;
    };

    // 행 1개 생성 (거점/품목군 단위) — 미사용 후보에서 다음 대상 선택, params 는 프리셋 재사용
    const makeRow = (mode, params, usedCds) => {
      const m = RP_SCOPE_META[mode];
      const cands = targets[m.candKey] || [];
      const used = usedCds ?? new Set();
      const next = cands.find(t => !used.has(t[m.cdKey]));
      return mkRow(mode, next?.[m.cdKey], next?.[m.nameKey], next?.reason, params);
    };

    // 단위 전환 — 기존 행 초기화 + 프리셋 값으로 재구성
    const setScopeMode = (mode) => {
      if (mode === scopeMode) return;
      const base = presetParams();
      if (mode === "all") {
        setOverrides([mkRow("all", null, null, null, base)]);
        return;
      }
      // 거점/품목군 — SHORT 최상위 후보 1건만 제안 (추가는 "+ 추가" 버튼)
      const m = RP_SCOPE_META[mode];
      const cands = targets[m.candKey] || [];
      const rows = (cands.length ? cands.slice(0, 1) : [null]).map(row =>
        mkRow(mode, row?.[m.cdKey], row?.[m.nameKey], row?.reason, { ...base }));
      setOverrides(rows);
    };

    // 후보(problem_targets) 총 개수 — 추가 가능 상한. 행 수가 이 값에 도달하면 더 못 추가.
    const candCount = optionsFor(scopeMode).length;
    const canAdd = overrides.length < candCount;

    const addOv = () => {
      if (!canAdd) return;   // 후보 소진 — 상한 초과 추가 차단
      const base = overrides.length ? { ...overrides[overrides.length - 1].params } : {};
      const meta = RP_SCOPE_META[scopeMode];
      const used = new Set(overrides.map(o => o[meta.cdKey]));
      setOverrides([...overrides, makeRow(scopeMode, base, used)]);
    };

    const codeSelect = (value, options, placeholder, onChange) => (
      <Box component="select" value={value ?? ""} disabled={disabled}
        onChange={e => onChange(e.target.value)}
        sx={{ ...inputSx, cursor: disabled ? "not-allowed" : "pointer" }}>
        <option value="">{placeholder}</option>
        {/* 현재값이 후보 목록에 없으면 그대로 옵션으로 유지 */}
        {value && !options.some(o => o.cd === value) && <option value={value}>{value}</option>}
        {options.map(o => (
          <option key={o.cd} value={o.cd}>
            {o.cd}{o.name ? ` (${o.name})` : ""}
          </option>
        ))}
      </Box>
    );

    const isAll = scopeMode === "all";
    const meta = RP_SCOPE_META[scopeMode]; // isAll 이면 undefined(아래 non-all 분기에서만 참조)
    const unitLabel = meta ? t(meta.label) : "";
    // 레거시 폴백 — 복원된 시나리오가 scope_type:"item" 인 동안에만 토글에 노출.
    // 다른 단위로 전환하면(스코프 갱신) scopeMode 가 바뀌어 다음 렌더에서 사라짐(단방향).
    const scopeOptionsList = scopeMode === "item"
      ? [OV_SCOPE_OPTIONS[0], OV_SCOPE_OPTIONS[1], { value: "item", label: "item" }, OV_SCOPE_OPTIONS[2]]
      : OV_SCOPE_OPTIONS;
    return (
      <Stack spacing={1}>
        {/* 시나리오 단위 선택기 — 전체/거점/품목군 세그먼트 토글 (행 단위 혼재 방지) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text3,
                            flex: "0 0 auto", letterSpacing: "0.02em" }}>{t("rpApplyScopeLabel")}</Typography>
          <AiSegmentedControl accent={accent} disabled={disabled} value={scopeMode}
            options={scopeOptionsList.map(o => ({ value: o.value, label: t(o.label) }))}
            onChange={setScopeMode} />
        </Box>

        {isAll ? (() => {
          /* 전체 — 모든 거점·품목 일괄 적용 단일 파라미터 블록 */
          const uidOf = (d) => d.param_key;
          const appliedOf = (d) => hasEffectiveValue(d, overrides[0]?.params?.[d.param_key]);
          const shown = scenario.param_defs.filter(d => isVisible(uidOf(d), appliedOf(d)));
          const cands = addCandidates(scenario.param_defs, uidOf, appliedOf);
          return (
          <Box>
            <Box sx={paramGroupHeaderSx}>
              <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2, flex: "0 0 auto" }}>
                {t("rpApplyAllScopeLabel")}
              </Typography>
              <Box sx={paramGroupRuleSx} />
            </Box>
            <Box>
              {shown.map((def, i) =>
                accordionParam(def.param_key, def, overrides[0]?.params?.[def.param_key],
                  // 전체 모드에서 overrides 가 아직 비어 있으면(직접 입력 초기 상태) 첫 편집 시 all 행을 생성한다.
                  (key, val) => {
                    if (!overrides.length) {
                      setOverrides([mkRow("all", null, null, null, { [key]: val })]);
                    } else {
                      patchOvParam(0, key, val);
                    }
                  }, fmtBaselineCurrent(def), i === shown.length - 1))}
            </Box>
            {view === "changed" && !disabled && (
              <Box sx={{ mt: 0.6 }}>
                <ScenarioParamAddPicker items={cands} accent={accent} onPick={pinAndOpen} />
              </Box>
            )}
          </Box>
          );
        })() : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Typography sx={{ fontSize: FONT.CAPTION, fontWeight: 700, color: C.text2 }}>
                {t("changeTargetLabel")}
              </Typography>
              <Box sx={{ minWidth: 16, height: 16, px: "5px", borderRadius: "8px",
                         bgcolor: `${accent}16`, color: accent, fontSize: FONT.MICRO, fontWeight: 700,
                         display: "flex", alignItems: "center", justifyContent: "center" }}>
                {overrides.length}
              </Box>
            </Box>
            {overrides.length === 0 && (
              <Box sx={{ p: "12px 10px", borderRadius: PARAM_CARD_RADIUS, textAlign: "center",
                         border: `1px dashed ${C.border}`, bgcolor: C.bg3 }}>
                <Typography sx={{ fontSize: FONT.CAPTION, color: C.text3 }}>
                  {t("rpNoTargetsHint", { unit: unitLabel })}
                </Typography>
              </Box>
            )}
            {overrides.map((ov, i) => {
              // 다른 행이 이미 선택한 코드 — 중복 선택 방지용. 본인 값은 유지.
              const usedByOthers = new Set(
                overrides.filter((_, j) => j !== i)
                  .map(o => o[meta.cdKey])
                  .filter(Boolean));
              const rowOptions = optionsFor(scopeMode).filter(o => !usedByOthers.has(o.cd));
              return (
              <Box key={i}
                sx={{ p: "10px 11px", borderRadius: PARAM_CARD_RADIUS, bgcolor: C.bg2,
                      border: `1px solid ${accent}33`,
                      boxShadow: `0 1px 2px ${C.text1}08` }}>
                <Box sx={{ display: "flex", gap: 0.6, alignItems: "center", mb: 0.7 }}>
                  <Box sx={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: 0.4,
                             height: 22, px: "7px", borderRadius: "7px",
                             bgcolor: `${accent}12`, color: accent,
                             fontSize: FONT.MICRO, fontWeight: 700 }}>
                    {unitLabel}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {codeSelect(ov[meta.cdKey], rowOptions, t(meta.placeholderKey),
                      v => patchOv(i, { [meta.cdKey]: v, [meta.nameKey]: undefined,
                                        reason: reasonFor(scopeMode)[v] ?? null }))}
                  </Box>
                  <Box component="button" type="button" disabled={disabled}
                    onClick={() => removeOv(i)} title={t("deleteTargetTooltip")}
                    sx={{ flex: "0 0 auto", width: 22, height: 22, borderRadius: "6px", border: "none",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: "transparent", color: C.text3, cursor: disabled ? "not-allowed" : "pointer",
                          fontSize: FONT.BODY, lineHeight: 1, transition: "background-color .12s, color .12s",
                          "&:hover": { bgcolor: C.g3l, color: C.g3 } }}>✕</Box>
                </Box>
                {(() => {
                  const cd  = ov[meta.cdKey];
                  const cur = currentFor(scopeMode)[cd];
                  const uidOf = (d) => `${cd ?? i}:${d.param_key}`;
                  const appliedOf = (d) => hasEffectiveValue(d, ov.params?.[d.param_key]);
                  const shown = scenario.param_defs.filter(d => isVisible(uidOf(d), appliedOf(d)));
                  const cands = addCandidates(scenario.param_defs, uidOf, appliedOf);
                  return (
                  <>
                    <Box>
                      {shown.map((def, di) =>
                        accordionParam(uidOf(def), def, ov.params?.[def.param_key],
                          (key, val) => patchOvParam(i, key, val), fmtCurrent(def, cur),
                          di === shown.length - 1))}
                    </Box>
                    {view === "changed" && !disabled && (
                      <Box sx={{ mt: 0.6 }}>
                        <ScenarioParamAddPicker items={cands} accent={accent} onPick={pinAndOpen} />
                      </Box>
                    )}
                  </>
                  );
                })()}
                {reasonTextOf(scopeMode, ov[meta.cdKey], ov.reason) && (
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "baseline", mt: 0.7,
                             pt: 0.6, borderTop: `1px dashed ${C.border}` }}>
                    <Typography sx={{ fontSize: FONT.MICRO, fontWeight: 700, color: C.text3, flex: "0 0 auto" }}>{t("reasonLabel")}</Typography>
                    <Typography sx={{ fontSize: FONT.CAPTION, color: C.g2t }}>
                      {reasonTextOf(scopeMode, ov[meta.cdKey], ov.reason)}
                    </Typography>
                  </Box>
                )}
              </Box>
              );
            })}
            <Box component="button" type="button" disabled={disabled || !canAdd} onClick={addOv}
              title={!canAdd ? t("rpCandAllAddedTooltip", { unit: unitLabel, count: candCount }) : undefined}
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.4,
                    width: "100%", border: `1px dashed ${accent}70`, borderRadius: PARAM_CARD_RADIUS,
                    bgcolor: "transparent", color: accent, fontSize: FONT.CAPTION, fontWeight: 700,
                    fontFamily: "inherit", p: "7px 8px",
                    opacity: (disabled || !canAdd) ? 0.45 : 1,
                    cursor: (disabled || !canAdd) ? "not-allowed" : "pointer",
                    transition: "background-color .12s, border-color .12s",
                    "&:hover": (disabled || !canAdd) ? {} : { bgcolor: `${accent}0E`, borderColor: accent } }}>
              <Box component="span" sx={{ fontSize: FONT.SUB, lineHeight: 1 }}>＋</Box>
              {!canAdd ? t("rpCandAllAddedLabel", { unit: unitLabel, count: candCount }) : t("rpAddUnitLabel", { unit: unitLabel })}
            </Box>
          </>
        )}
      </Stack>
    );
  }

  // param_defs 가 없으면 렌더할 편집 항목이 없음(직접 입력 카드도 paramDefsUnion 으로 param_defs 주입됨).
  // 레거시 RP_MODULE.params 고정 3종 폴백은 제거됨.
  return null;
}

export const RP_MODULE = {
  code: "RP",
  // ── 비교 탭 "품목 수준 차이"(CompareItemDiff) 설정 — grain 은 (도착거점,출발거점,품목) ──
  itemDiffCfg: {
    locKey: "dest_loc_cd",
    columns: [
      { key: "fill_rate",         labelKey: "itemDiffColFillRate",     ko: "충족률(%)",   digits: 1, dir: "up" },
      { key: "inv_coverage_days", labelKey: "itemDiffColCoverage",     ko: "커버일수(일)", digits: 1, dir: null },
      { key: "stockout_days",     labelKey: "itemDiffColStockoutDays", ko: "결품일수(일)", digits: 0, dir: "down" },
    ],
    // 행별 분류 근거 배지 — 위험도 전이가 없을 때 primary(fill_rate) delta 표기용 축약 라벨.
    primaryReason: { labelKey: "itemDiffReasonFillShort", ko: "충족률", unit: "%p" },
    // 품목명 클릭 → 보충계획 결과(RP_REVIEW) (비교 시나리오 버전 + 도착거점/품목 필터 주입)
    makeDrillTargets: (dispatch, row, version) => [{
      label: i18n.t("menuRpReview"),
      onClick: () => openRpReview(
        dispatch, { locCd: row.dest_loc_cd, itemCd: row.item_cd }, version),
    }],
  },
  // ── 비교 탭 산점도/핵심차이 카드 클릭 → 해당 시나리오 버전의 보충계획 결과(RP_REVIEW) 오픈(필터 없이 전체) ──
  scenarioDrill: (dispatch, version) => openRpReview(dispatch, {}, version),
  // ── 시나리오 구성 탭 "예상 효과" 미리보기 필드(엔진 실행 전) — expected_effects 키 매핑 ──
  //   RP 백엔드(rp/llm_client.py)가 fill_rate_change_pct/lead_time_change_days/cost_change_pct 방출.
  //   goodSign: +1=값이 클수록 개선(녹색 g1), -1=값이 작을수록 개선. badColor: 악화 시 색(g2 경고/g3 위험).
  expectedEffectsFields: [
    { key: "fill_rate_change_pct",  labelKey: "fillRateColonLabel", unit: "%p",         goodSign: 1,  badColor: "g3" },
    { key: "lead_time_change_days", labelKey: "leadTimeColonLabel", unitKey: "dayUnit", goodSign: -1, badColor: "g2" },
    { key: "cost_change_pct",       labelKey: "costColonLabel",     unit: "%",          goodSign: -1, badColor: "g2" },
  ],
  // ── Analysis 탭 Quick Prompt (자유 질문, 액션 없음) — { i18nKey, ko } ──────────
  quickPrompts: {
    analysis: {
      0: [
        { i18nKey: "agentAskRpKpi",     ko: "현황 KPI 해석해줘" },
        { i18nKey: "agentAskRpStockout",ko: "결품 위험 품목은?" },
        { i18nKey: "agentAskRpExcess",  ko: "과잉 보충 품목은?" },
        { i18nKey: "agentAskRpDiagnose",ko: "결품 원인 진단해줘" },
      ],
      1: [
        { i18nKey: "agentAskRpPolicy",  ko: "보충 정책 차이는?" },
        { i18nKey: "agentAskRpSsImpact",ko: "안전재고 배율 영향은?" },
      ],
      2: [
        { i18nKey: "agentAskRpBest",    ko: "어떤 시나리오가 최적이야?" },
        { i18nKey: "agentAskRpFillRate",ko: "서비스레벨 비교" },
      ],
      3: [
        { i18nKey: "agentAskRpChecklist", ko: "확정 전 체크리스트" },
      ],
    },
  },

  // ── Action 탭: 화면 하위상태(step·mode·aiResult)에 맞는 "지금 가능한 액션"만 선언 (DP 와 동일 패턴) ──
  // 값 고정 set_param 프리셋은 clickOnly(자유입력 dispatch 제외). 자유입력 파라미터 변경은 set_param 값 추출 경로가 처리.
  // description/confirmKo 는 모듈 레벨 함수(컴포넌트 아님)라 useTranslation 훅을 쓸 수 없어
  //   i18n 싱글턴(i18n.t)으로 미리 번역해 둔다 (IP/DP agentActions 와 동일 패턴).
  agentActions: ({ step, mode, aiResult, exec }) => {
    const selMode = (m, key, ko) => {
      const modeLabel = i18n.t(key, { defaultValue: ko });
      return {
        id: `rp.mode.${m}`, i18nKey: key, ko, group: "action", style: "secondary",
        description: i18n.t("ipSelectModeDesc", { mode: modeLabel }),
        run: () => exec({ action: "select_mode", payload: { mode: m } }),
        confirmKo: i18n.t("ipSelectModeConfirm", { mode: modeLabel }),
      };
    };
    // 값이 고정된 파라미터 프리셋(MOQ 1.2배 등)은 제거했다 — 그 수치의 근거가 없고 사용자가
    //   원하는 값이라는 보장도 없다. 파라미터 변경은 컨테이너 공통 scn.param.change 가
    //   시나리오→항목→값(선택지·직접 입력) 연속 선택으로 받는다.
    const runEngine = {
      id: "rp.engine.run", i18nKey: "agentActRpRunEngine", ko: "엔진 실행",
      group: "action", style: "primary", description: i18n.t("rpRunEngineDesc"),
      run: () => exec({ action: "run_engine" }),
      confirmKo: i18n.t("MSG_IpEngineRunConfirm"),
    };
    if (step === 0) return [
      selMode("ai",     "agentActRpModeAi",     "AI 추천 시나리오 생성"),
      selMode("custom", "agentActRpModeCustom", "표준 시나리오 선택"),
    ];
    if (step === 1) {
      // mode 미설정(null)은 표준 시나리오 선택 상태와 동일(통합 뷰 기본) — 초기 방식 선택 액션 없이
      //   표준 경로 액션을 그대로 노출. AI 전환은 컨테이너 공통 scn.mode.ai 가 제공.
      if (mode === "ai" && !aiResult) return [];
      return [runEngine];
    }
    return [];   // 단계 이동은 컨테이너 generic nav(nav.step*)가 전담 — 모듈별 고정 navTo 는 라벨 중복이라 제거
  },
  // label/step2lbl/step4lbl/initInputs/outputs 값은 i18n 키 문자열 — 모듈 레벨 상수라 t()를 직접
  //   쓸 수 없으므로 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  label: "rpModuleLabel",
  Icon: LocalShippingIcon,
  skipKpiSection: true,   // RpExtraInitContent에서 현황 KPI를 직접 표시하므로 KPI 요약 카드 숨김
  step2lbl: "dpStep2Label",
  step4lbl: "rpStep4Label",
  inputFrom: "IP",

  initInputs: ["rpInitInputReleased", "rpInitInputKpi", "rpInitInputFillRateLt", "rpInitInputNetwork"],

  // ★ kpis / bestMap / aiRec → Agent 동적 반환 (하드코딩 금지)
  // scenarios: Tab1/3/4(현황·비교·확정) 표시용 기준 시나리오 셰이프 폴백(엔진 결과 채워지기 전).
  // 표준 시나리오는 fetchTemplates(z_ai_scenario DB 카탈로그)가 제공하고, 직접 입력 시나리오는
  // 그 param_defs 합집합(공통 paramDefsUnion)을 빈 값으로 편집한다. 레거시 params 목업 편집 파라미터 제거됨.
  scenarios: [
    {
      role: "base", name: "scnRpBaseName",
      desc: "scnRpBaseDesc",
      tags: ["scnTagStandardOperation", "scnTagBalancedInventory", "scnTagStableReplenishment"],
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-", e: "-", f: "-" },
    },
    {
      role: "alt1", name: "scnRpAlt1Name",
      desc: "scnRpAlt1Desc",
      tags: ["scnTagCostSaving", "scnTagLeanInventory", "scnTagMinHolding"],
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-", e: "-", f: "-" },
    },
    {
      role: "alt2", name: "scnRpAlt2Name",
      desc: "scnRpAlt2Desc",
      tags: ["scnTagHighFillRate", "scnTagStockoutPrevention", "scnTagSafetyStockBoost"],
      score: 0, kpis: { a: "-", b: "-", c: "-", d: "-", e: "-", f: "-" },
    },
  ],

  kpiLabels: ["rpKpiTotalOrderQtyLabel", "rpKpiLeadTimeLabel", "rpKpiFillRateLabel", "replenishPlanAmt", "rpKpiSafetyShortLabel", "rpKpiHoldingCostLabel"],
  kpiKeys:   ["a", "b", "c", "d", "e", "f"],
  bestMap:   { a: "alt1", c: "alt2", d: "alt1", e: "alt2", f: "alt1" },
  // KPI 방향: 비교 표가 표시값으로 최적을 산출(엔진 bestMap 부재 시 방향 무시 폴백 차단).
  // c=공급 충족률은 높을수록, d=보충계획 금액·e=결품 건수·f=재고 보관 비용은 낮을수록 좋음.
  // a=총 발주량 중립. b=보충 리드타임은 사용자 입력값이라 평가 제외(중립).
  kpiDir:    { a: "neutral", b: "neutral", c: "max", d: "min", e: "min", f: "min" },
  moneyKpiKeys: ["d", "f"],

  // kpiMeta.label/insight/note 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  kpiMeta: [
    {
      key: "a", icon: "", label: "rpKpiTotalOrderQtyLabel",
      getStatus: () => "neutral",
      insight: "rpKpiInsightTotalOrderQty",
      note: "rpKpiNoteNeutral",
    },
    {
      // 보충 리드타임은 사용자 입력 파라미터 → 계획 품질 평가에서 제외(중립).
      key: "b", icon: "", label: "rpKpiLeadTimeLabel",
      getStatus: () => "neutral",
      insight: "rpKpiInsightLeadTime",
      note: "rpKpiNoteNeutral",
    },
    {
      key: "c", icon: "", label: "rpKpiFillRateLabel",
      getStatus: (v) => {
        const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
        if (isNaN(n)) return "neutral";
        return n >= 98 ? "good" : n >= 95 ? "near" : n >= 90 ? "warn" : "bad";
      },
      insight: "rpKpiInsightFillRate",
      note: "ipTargetSl95PlusNote",
    },
    {
      key: "d", icon: "", label: "replenishPlanAmt",
      getStatus: () => "neutral",
      insight: "rpKpiInsightOrderAmt",
      note: "rpKpiNoteOrderAmt",
    },
    {
      // 안전재고 미달 건수: 기말재고가 안전재고 밑으로 떨어진 SKU 수(eoh<safety). 결품(음수재고)보다 넓은 리스크.
      key: "e", icon: "", label: "rpKpiSafetyShortLabel",
      getStatus: (v) => {
        const n = parseFloat(String(v).replace(/[^0-9.]/g, ""));
        if (isNaN(n)) return "neutral";
        return n <= 0 ? "good" : n <= 3 ? "warn" : "bad";
      },
      insight: "rpKpiInsightSafetyShort",
      note: "rpKpiNoteSafetyShort",
    },
    {
      // 재고 보관 비용(재고 금액 프록시) — 많을수록 감점(dir min). 절대 임계는
      // 통화·규모 의존이라 상태색 중립, 최적은 bestMap(f=최소)으로만 강조.
      key: "f", icon: "", label: "rpKpiHoldingCostLabel",
      getStatus: () => "neutral",
      insight: "rpKpiInsightHoldingCost",
      note: "rpKpiNoteHoldingCost",
    },
  ],

  // 흐름도 배선 — 어느 단계가 시나리오 고유인지, 세로로 몇 노드가 쌓이는지.
  //   flowTopNodes = 공통 3(현황·시나리오 확정·KPI 해석) + 분기 앵커 1.
  engineTrack: { parentTool: "rp_step3_engine", childPrefix: "rp_engine_",
                 match: "role", flowTopNodes: 4 },
  // enginePhases 값은 i18n 키 문자열 — 소비처에서 t()로 변환한다.
  enginePhases: [
    "rpEnginePhaseIpLoad",
    "rpEnginePhaseRopIdentify",
    "rpEnginePhaseSourcePriority",
    "rpEnginePhaseCapaCheck",
    "rpEnginePhaseAlloc",
    "complete",
  ],

  aiRec: 2,

  // AI 추천 시나리오 안내 (공유 컴포넌트에서 모듈별로 렌더, 미지정 모듈은 기존 DP/BF 문구 사용)
  // 값은 i18n 키 문자열 — 소비처(AiScenarioPlanning.js)에서 t()로 변환한다.
  aiRecCardDesc: "rpAiRecCardDesc",
  aiRecIntroLines: [
    "rpAiRecIntroLine1",
    "rpAiRecIntroLine2",
  ],
  aiRecFactors: ["rpAiRecFactorFillRate", "rpAiRecFactorLeadTime", "rpAiRecFactorSsFulfill", "rpAiRecFactorOrderInvLevel"],
  aiRecLoadingDesc: "rpAiRecLoadingDesc",
  // agentActRpModeAi 의 ko("AI 추천 시나리오 생성")와 동일 문구라 동일 키를 재사용한다.
  aiRecButtonLabel: "agentActRpModeAi",
};

// RP_MODULE에 ExtraInitContent 등록 (선언 후에 할당)
RP_MODULE.ExtraInitContent = RpExtraInitContent;

// ── 실현 가능성 경고 (비교 탭) — 백엔드 결정형 판정(scn.feasibility_warnings)을 공용 위젯이 렌더.
//   RP 는 R-RP-01(수송 LT 0일)·R-RP-02(대폭 단축)·R-RP-03(MOQ 하향). 전부 공급사 계약이 소유한 제약.
RP_MODULE.feasibilityCfg = { sectionTitleKey: "feasWarnSectionTitle" };
RP_MODULE.scenarioWarnCount = feasWarnCount;   // KPI 비교표 '경고' 행(공용 슬롯, tone 만 전달)
RP_MODULE.RecHeroBadges = FeasibilityHeroBadges;
// 시나리오 구성 탭(파라미터·엔진)의 RP 전용 시각화 — 공통 StepParamEngine 이 주입받아 렌더
RP_MODULE.ComposeExtraContent = RpComposeExtraContent;
// 시나리오 카드 파라미터 위젯 — param_defs 동적 렌더 + 레거시(mod.params) 폴백
RP_MODULE.CustomScenarioFields = RpScenarioParamFields;
// RP 는 param_defs 를 자체 렌더(overrides·problem_targets·current_display 포함)하므로
// 공통 EditableScenarioCards 의 제네릭 param_defs 분기를 우회한다.
RP_MODULE.handlesParamDefs = true;

// 표준 시나리오 동적 로딩 (z_ai_scenario 카탈로그 — 기존 하드코딩 templates 대체).
// 공통 StepScenarioCreate 가 mod.fetchTemplates 존재 시 이 결과를 템플릿 카드로 렌더한다.
// 시나리오 1개 선택 → variants(base=무변경 / alt1=보수적 / alt2=과감) 3개 변형 카드 표시.
RP_MODULE.fetchTemplates = async () => {
  const res = await scenarioService.getRpScenarios();
  rememberModuleCatalog(RP_MODULE, res);   // 표준 시나리오 0건이어도 직접 입력 카드가 그려지도록 최상위 카탈로그 보관
  const problemTargets = res?.problem_targets || {};
  return (res?.scenarios || []).map(scn => ({
    id:          scn.scenario_cd,
    industry:    scn.source_type === "USER" ? "userTagLabel"
                 : (scn.recommended_flg === "Y" ? "recommendedTagLabel" : "standardTagLabel"),
    // 표시용 i18n 키(*_key)를 우선 사용, 없으면 원문 폴백. LLM 은 원문을 그대로 소비.
    name:        scn.scenario_name_key || scn.scenario_name,
    direction:   scn.scenario_direction_key || scn.scenario_direction,
    useCase:     scn.desc_txt_key || scn.desc_txt,   // desc 요약 — 카드 본문(이슈상황은 그룹 내 동일하여 미노출)
    desc:        scn.desc_txt_key || scn.desc_txt,
    recommended: scn.recommended_flg === "Y",
    // base/alt1/alt2 — params + param_defs + overrides 임베드.
    // problem_targets(SHORT 문제 거점/품목)는 "대상 추가" 후보로 각 변형에 주입.
    scenarios:   (scn.variants || []).map(v => ({ ...v, problem_targets: problemTargets })),
  }));
};

// ── RP 시나리오 플래닝 페이지 ──────────────────────────────────────────────
// AiScenarioPlanning 을 lazy import 하여 순환 참조 방지
const AiScenarioPlanningLazy = React.lazy(() => import("./AiScenarioPlanning"));

export default function AiRpScenarioPlanning({ viewName }) {
  return (
    <React.Suspense fallback={null}>
      <AiScenarioPlanningLazy viewName={viewName} singleModuleCode="RP" />
    </React.Suspense>
  );
}

